import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { 
  Sparkles, CheckCircle2, TrendingUp, Zap, Clock, Euro, 
  Target, Shield, Star, ChevronDown, Image as ImageIcon,
  FileText, Palette, MessageSquare, Phone, ArrowRight, Calendar, Calculator
} from 'lucide-react';
import axios from 'axios';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';

const LandingPageV2 = ({ onLogin, openLoginModal = false }) => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(openLoginModal);
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Pricing Toggle State
  const [pricingMode, setPricingMode] = useState('lifetime'); // 'monthly' or 'lifetime'
  const [includeTargeting, setIncludeTargeting] = useState(false); // Targeting Helper Add-on
  
  // Aha-Effekt Widget State
  const [selectedSparte, setSelectedSparte] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResult, setShowResult] = useState(false);
  
  // Widget Sparten-Daten mit professionellen Nano Banana Creatives (nach JSON-Briefing)
  const spartenData = {
    zahnzusatz: {
      name: "Zahnzusatz",
      icon: "🦷",
      headline: "Zahn-Schutz 100%",
      subheadline: "Keine Eigenanteile mehr.",
      body: "Professionelle Implantate, Kronen, Brücken – die Rechnung vom Zahnarzt kann schnell 5.000€+ kosten. Mit der richtigen Zahnzusatzversicherung zahlst du fast nichts.",
      cta: "Jetzt kostenlos beraten lassen",
      benefits: ["✅ Bis zu 100% Kostenübernahme", "✅ Keine Wartezeit bei Unfall", "✅ Sofort-Schutz verfügbar"],
      color: "from-cyan-500 to-blue-600",
      image: "https://customer-assets.emergentagent.com/job_eliteplan-upgrade/artifacts/dv7k858f_Zahnschutz2.webp"
    },
    bu: {
      name: "Berufsunfähigkeit",
      icon: "🛡️",
      headline: "Einkommen gesichert",
      subheadline: "Dein Gehalt – auch wenn du nicht mehr kannst.",
      body: "Jeder 4. wird berufsunfähig. Die staatliche Absicherung? Ein Witz. Sichere dich jetzt ab – bevor es zu spät ist.",
      cta: "Jetzt BU-Schutz prüfen",
      benefits: ["✅ Bis zu 75% des Einkommens", "✅ Flexible Laufzeiten", "✅ Nachversicherungsgarantie"],
      color: "from-amber-600 to-orange-700",
      image: "https://customer-assets.emergentagent.com/job_eliteplan-upgrade/artifacts/wzntw9wo_Berufsunf%C3%A4higkeit%20%202.webp"
    },
    pkv: {
      name: "Private Krankenversicherung",
      icon: "🏥",
      headline: "Privat-Status",
      subheadline: "Chefarzt. Einzelzimmer. Kurze Wartezeiten.",
      body: "Chefarztbehandlung, Einzelzimmer, kurze Wartezeiten – all das für oft weniger als in der GKV. Finde heraus, ob du wechseln kannst.",
      cta: "Jetzt PKV-Check starten",
      benefits: ["✅ Bis zu 50% sparen vs. GKV", "✅ Bessere Leistungen garantiert", "✅ Beitragsrückerstattung"],
      color: "from-violet-500 to-purple-600",
      image: "https://customer-assets.emergentagent.com/job_eliteplan-upgrade/artifacts/nz8f76vi_PKV.webp"
    },
    hundeop: {
      name: "Hunde-OP",
      icon: "🐕",
      headline: "Hunde-Schutz",
      subheadline: "Bester Schutz für deinen Liebling.",
      body: "Eine Kreuzband-OP beim Hund kostet schnell 3.000€+. Mit der richtigen Versicherung zahlst du nur einen Bruchteil.",
      cta: "Jetzt Fellnase absichern",
      benefits: ["✅ Bis zu 100% OP-Kosten", "✅ Freie Tierarztwahl", "✅ Keine Gesundheitsprüfung"],
      color: "from-amber-500 to-yellow-600",
      image: "https://customer-assets.emergentagent.com/job_eliteplan-upgrade/artifacts/gxvv9bv9_Hundeversicherung.webp"
    },
    katze: {
      name: "Katzen-OP",
      icon: "🐱",
      headline: "Für deine Katze.",
      subheadline: "Katzenversicherung ohne Stress.",
      body: "Dein Stubentiger ist mehr als nur ein Haustier. Schütze ihn vor unerwarteten Tierarztkosten – ab nur wenigen Euro im Monat.",
      cta: "Jetzt Samtpfote absichern",
      benefits: ["✅ OP-Kosten bis 100%", "✅ Freie Tierarztwahl", "✅ Schnelle Erstattung"],
      color: "from-orange-500 to-red-500",
      image: "https://customer-assets.emergentagent.com/job_eliteplan-upgrade/artifacts/t1jgnv9q_Katzenversicherung.webp"
    },
    altersvorsorge: {
      name: "Altersvorsorge",
      icon: "👴",
      headline: "Deine Rente.",
      subheadline: "Mit der richtigen Vorsorge.",
      body: "Die gesetzliche Rente wird nicht reichen. Starte jetzt mit einer privaten Altersvorsorge und genieße deinen Ruhestand sorgenfrei.",
      cta: "Jetzt Vorsorge planen",
      benefits: ["✅ Staatliche Förderung nutzen", "✅ Flexible Einzahlungen", "✅ Lebenslange Rente"],
      color: "from-emerald-500 to-green-600",
      image: "https://customer-assets.emergentagent.com/job_eliteplan-upgrade/artifacts/zm8dsyez_Altersvorsorge.webp"
    },
    rechtsschutz: {
      name: "Rechtsschutz",
      icon: "⚖️",
      headline: "Dein Recht.",
      subheadline: "Rechtsschutz der sich lohnt.",
      body: "Ärger mit dem Arbeitgeber, Nachbarn oder Vermieter? Mit einer Rechtsschutzversicherung kämpfst du um dein Recht – ohne Kostenrisiko.",
      cta: "Jetzt absichern",
      benefits: ["✅ Arbeitsrecht inklusive", "✅ Verkehrsrecht inklusive", "✅ Freie Anwaltswahl"],
      color: "from-blue-600 to-indigo-700",
      image: "https://customer-assets.emergentagent.com/job_eliteplan-upgrade/artifacts/cqswv5zq_Rechtsschutz.webp"
    },
    immobilie: {
      name: "Baufinanzierung",
      icon: "🏠",
      headline: "Endlich Eigentum",
      subheadline: "Dein Traum wird wahr.",
      body: "Der Traum vom Eigenheim muss kein Traum bleiben. Finde die beste Baufinanzierung und spare tausende Euro an Zinsen.",
      cta: "Jetzt Zinsen vergleichen",
      benefits: ["✅ Beste Konditionen", "✅ Bis zu 30 Jahre Zinsbindung", "✅ Kostenlose Beratung"],
      color: "from-rose-500 to-pink-600",
      image: "https://customer-assets.emergentagent.com/job_eliteplan-upgrade/artifacts/g017kzk6_Immobilienfinanzierung.webp"
    },
    gold: {
      name: "Goldsparpläne",
      icon: "🪙",
      headline: "Sicheres Gold",
      subheadline: "Inflationsschutz für dein Vermögen.",
      body: "Gold ist seit Jahrtausenden ein sicherer Hafen. Starte jetzt mit einem Goldsparplan und sichere dein Vermögen gegen Inflation.",
      cta: "Jetzt Gold sichern",
      benefits: ["✅ Physisches Gold", "✅ Flexible Sparraten", "✅ Bankenunabhängig"],
      color: "from-yellow-500 to-amber-600",
      image: "https://customer-assets.emergentagent.com/job_eliteplan-upgrade/artifacts/0d3zjd8c_Goldsparplan.webp"
    },
    kfz: {
      name: "Kfz-Versicherung",
      icon: "🚗",
      headline: "Top Kfz-Tarif",
      subheadline: "Bester Schutz für dein Auto.",
      body: "Kfz-Versicherung muss nicht teuer sein. Vergleiche jetzt und spare bis zu 50% bei gleicher Leistung.",
      cta: "Jetzt Tarif vergleichen",
      benefits: ["✅ Bis zu 50% sparen", "✅ Schnelle Schadenabwicklung", "✅ Flexible Selbstbeteiligung"],
      color: "from-slate-600 to-gray-700",
      image: "https://customer-assets.emergentagent.com/job_eliteplan-upgrade/artifacts/gog4czlr_PKV%20Vergleich.webp"
    }
  };
  
  // Widget Handler
  const handleSparteSelect = (sparte) => {
    setSelectedSparte(sparte);
    setShowResult(false);
    setIsGenerating(true);
    
    // Simuliere KI-Generierung
    setTimeout(() => {
      setIsGenerating(false);
      setShowResult(true);
    }, 1800);
  };
  
  // Social Proof Banner State (for the green banner)
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  
  // Social Proof Notification State
  const [showNotification, setShowNotification] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);
  
  // Stripe Checkout Handler - DIREKT zu Stripe ohne Backend
  const handleCheckout = async (plan) => {
    console.log('=== CHECKOUT STARTED (Direct Stripe) ===', plan);
    setCheckoutLoading(true);
    
    // Direkte Stripe Payment Links - KEIN Backend nötig!
    const STRIPE_LINKS = {
      // Basis-Pläne
      lifetime: 'https://buy.stripe.com/8x29AU1FJag1dEJ4yA3F603',
      monthly: 'https://buy.stripe.com/dRm5kE0BFag1fMR9SU3F604',
      
      // Mit Targeting Bundle
      lifetime_targeting_bundle: 'https://buy.stripe.com/7sYcN6gAD0FrgQV6GI3F605',
      monthly_targeting_bundle: 'https://buy.stripe.com/7sY00k8470FrbwBd563F606',
      
      // Elite Pläne
      meta_autopilot: 'https://buy.stripe.com/bJe00k2JN73PdEJ1mo3F607',
      meta_autopilot_monthly: 'https://buy.stripe.com/eVq14o98b0Fr0RX1mo3F608',
      meta_autopilot_targeting: 'https://buy.stripe.com/eVqdRadorag1eIN5CE3F609',
      meta_autopilot_monthly_targeting: 'https://buy.stripe.com/bJe7sM3NR3RDfMR0ik3F60a',
    };
    
    // Map plan names to subscription types
    let subscriptionType = plan;
    
    if (includeTargeting) {
      if (plan === 'lifetime') {
        subscriptionType = 'lifetime_targeting_bundle';
      } else if (plan === 'monthly') {
        subscriptionType = 'monthly_targeting_bundle';
      } else if (plan === 'elite') {
        subscriptionType = 'meta_autopilot_targeting';
      } else if (plan === 'elite_monthly') {
        subscriptionType = 'meta_autopilot_monthly_targeting';
      }
    } else {
      if (plan === 'elite') {
        subscriptionType = 'meta_autopilot';
      } else if (plan === 'elite_monthly') {
        subscriptionType = 'meta_autopilot_monthly';
      }
    }
    
    const stripeLink = STRIPE_LINKS[subscriptionType];
    
    if (stripeLink) {
      console.log('Redirecting directly to Stripe:', stripeLink);
      window.location.href = stripeLink;
    } else {
      console.error('No Stripe link found for:', subscriptionType);
      toast.error('Zahlungslink nicht gefunden. Bitte kontaktieren Sie den Support.');
      setCheckoutLoading(false);
    }
  };
  
  // Countdown Timer State
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Social Proof Notifications Data
  const notifications = [
    { name: "Thomas M.", city: "München", product: "Lifetime + Targeting Bundle", time: "vor 3 Minuten" },
    { name: "Sandra K.", city: "Hamburg", product: "Lifetime-Zugang (Beta-Rabatt)", time: "vor 8 Minuten" },
    { name: "Michael R.", city: "Berlin", product: "Lifetime + Targeting Bundle", time: "vor 12 Minuten" },
    { name: "Julia S.", city: "Köln", product: "Lifetime-Zugang (Beta-Rabatt)", time: "vor 15 Minuten" },
    { name: "Andreas W.", city: "Frankfurt", product: "Monatliches Abo", time: "vor 7 Minuten" },
    { name: "Christina L.", city: "Stuttgart", product: "Lifetime + Targeting Bundle", time: "vor 10 Minuten" },
    { name: "Markus B.", city: "Düsseldorf", product: "Lifetime-Zugang (Beta-Rabatt)", time: "vor 5 Minuten" },
    { name: "Laura H.", city: "Leipzig", product: "Lifetime + Targeting Bundle", time: "vor 20 Minuten" },
    { name: "Stefan P.", city: "Dresden", product: "Lifetime-Zugang (Beta-Rabatt)", time: "vor 14 Minuten" },
    { name: "Nicole F.", city: "Hannover", product: "Monatliches Abo", time: "vor 9 Minuten" },
    { name: "Felix K.", city: "Nürnberg", product: "Lifetime + Targeting Bundle", time: "vor 6 Minuten" },
    { name: "Sarah W.", city: "Bremen", product: "Lifetime-Zugang (Beta-Rabatt)", time: "vor 18 Minuten" },
    { name: "Daniel S.", city: "Essen", product: "Monatliches Abo", time: "vor 11 Minuten" },
    { name: "Petra M.", city: "Dortmund", product: "Lifetime + Targeting Bundle", time: "vor 16 Minuten" },
    { name: "Jan T.", city: "Bonn", product: "Lifetime-Zugang (Beta-Rabatt)", time: "vor 4 Minuten" }
  ];

  // Social Proof Notification Effect
  useEffect(() => {
    const showRandomNotification = () => {
      const randomNotif = notifications[Math.floor(Math.random() * notifications.length)];
      setCurrentNotification(randomNotif);
      setShowNotification(true);
      
      // Hide after 5 seconds
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    };

    // Show first notification after 8 seconds
    const initialTimeout = setTimeout(showRandomNotification, 8000);
    
    // Then show every 28 seconds
    const interval = setInterval(() => {
      showRandomNotification();
    }, 28000); // Every 28 seconds

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);

  // Green Banner Rotation Effect - Every 28 seconds
  useEffect(() => {
    const bannerInterval = setInterval(() => {
      setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % notifications.length);
    }, 28000); // Every 28 seconds

    return () => clearInterval(bannerInterval);
  }, []);

  // Set countdown to end of week (Sunday 23:59:59)
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const endOfWeek = new Date();
      endOfWeek.setDate(now.getDate() + (7 - now.getDay())); // Next Sunday
      endOfWeek.setHours(23, 59, 59, 999);
      
      const difference = endOfWeek - now;
      
      if (difference > 0) {
        return {
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
      }
      return { hours: 0, minutes: 0, seconds: 0 };
    };

    setTimeLeft(calculateTimeLeft());
    
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleStartClick = () => {
    // Track InitiateCheckout event
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout');
    }
    navigate('/pricing');
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axiosInstance.post('/auth/login', { username, password });
        toast.success('Login erfolgreich!');
        onLogin(response.data.access_token, response.data.username);
        // Navigate to dashboard after successful login
        navigate('/dashboard');
      } else {
        toast.error('Bitte nutzen du die Zahlungs-Seite für die Registrierung');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Login');
    } finally {
      setLoading(false);
    }
  };

  const faqs = [
    {
      q: "Was kostet FinAds Creator?",
      a: "79€ einmalig (statt 199€) für lebenslangen Zugang. Alternativ 19€/Monat flexibel kündbar. Optional: KI-Targeting Booster für +59€. Keine versteckten Kosten, keine Abo-Fallen."
    },
    {
      q: "Für welche Plattformen funktioniert das?",
      a: "Facebook, Instagram (Feed, Stories, Reels) und LinkedIn. Alle Formate werden automatisch in der richtigen Größe erstellt – du musst nur noch hochladen."
    },
    {
      q: "Sind meine Daten sicher?",
      a: "Ja. Alle Daten werden verschlüsselt gespeichert (SSL/AES-256). Wir verkaufen keine Daten an Dritte. Deine Creatives und Brand-Assets gehören dir."
    },
    {
      q: "Brauche ich Vorkenntnisse?",
      a: "Nein. 60% unserer Nutzer haben noch nie eine Ad erstellt. Du wählst ein Template, lädst dein Logo hoch – fertig. Die KI macht den Rest."
    },
    {
      q: "Was, wenn es nicht funktioniert?",
      a: "Für 79€ testest du ein System, das anderen 47 Termine in 2 Wochen gebracht hat. Selbst bei nur 5 Terminen hast du die Investition 10x zurück. Agenturen kosten 1.500€/Monat – wir einmalig 79€."
    }
  ];

  const templates = [
    { name: "Berufsunfähigkeit", emoji: "🛡️", category: "Lebensversicherung" },
    { name: "Rentenversicherung", emoji: "🏖️", category: "Lebensversicherung" },
    { name: "Krankenversicherung", emoji: "⚕️", category: "Krankenversicherung" },
    { name: "Zahnzusatz", emoji: "😁", category: "Krankenversicherung" },
    { name: "Kfz-Versicherung", emoji: "🚗", category: "Sachversicherung" },
    { name: "Hundeversicherung", emoji: "🐕", category: "Sachversicherung" },
    { name: "Katzenversicherung", emoji: "🐱", category: "Sachversicherung" },
    { name: "Immobilienfinanzierung", emoji: "🏡", category: "Finanzen" },
    { name: "Goldsparpläne", emoji: "🪙", category: "Finanzen" },
    { name: "Rechtsschutz", emoji: "⚖️", category: "Versicherung" }
  ];

  const testimonials = [
    {
      name: "Julius W.",
      role: "Selbstständiger Finanzberater, Dresden",
      text: "Ich hatte seit 3 Monaten keine einzige Anfrage mehr. War kurz davor aufzugeben. Mit FinAds habe ich in den ersten 2 Wochen 47 qualifizierte Termine generiert. Das Tool hat mir buchstäblich das Business gerettet.",
      rating: 5,
      result: "Von 0 auf 47 Termine in 14 Tagen",
      image: "https://customer-assets.emergentagent.com/job_minimal-adcraft/artifacts/wrv2yzan_Screenshot%20-%202025-10-30T082423.736.png"
    },
    {
      name: "Sabine T.",
      role: "Versicherungsmaklerin, Nürnberg",
      text: "Vorher: 85€ pro Lead für BU-Versicherungen über Google Ads. Mit FinAds: unter 18€! Ich kann es selbst kaum glauben. Die Templates funktionieren einfach.",
      rating: 5,
      result: "Lead-Kosten von 85€ auf 18€",
      image: "https://customer-assets.emergentagent.com/job_minimal-adcraft/artifacts/mb99zt91_Screenshot%20-%202025-10-30T082537.735.png"
    },
    {
      name: "Thomas M.",
      role: "Versicherungsmakler, München",
      text: "Früher habe ich 3.500€ pro Monat an eine Agentur gezahlt. Jetzt erstelle ich meine Ads selbst in Minuten. Die Templates für Versicherungsprodukte sind perfekt!",
      rating: 5,
      result: "Ersparnis: 3.481€/Monat",
      image: "https://customer-assets.emergentagent.com/job_minimal-adcraft/artifacts/12aglrdl_Screenshot%20-%202025-10-30T082441.402.png"
    },
    {
      name: "Sandra K.",
      role: "Finanzberaterin, Hamburg",
      text: "Als Einzelkämpferin kann ich mir keine teure Agentur leisten. Mit FinAds Creator sehen meine Ads trotzdem professionell aus. Meine Lead-Kosten sind um 60% gesunken!",
      rating: 5,
      result: "Lead-Kosten -60%",
      image: "https://customer-assets.emergentagent.com/job_minimal-adcraft/artifacts/mb99zt91_Screenshot%20-%202025-10-30T082537.735.png"
    },
    {
      name: "Michael R.",
      role: "Inhaber Versicherungsagentur, Berlin",
      text: "Wir erstellen jetzt Ads für all unsere Produkte - von BU bis Altersvorsorge. Die 20 Templates sparen uns Stunden an Arbeit. Absolute Empfehlung!",
      rating: 5,
      result: "5x mehr Ads produziert",
      image: "https://customer-assets.emergentagent.com/job_minimal-adcraft/artifacts/12aglrdl_Screenshot%20-%202025-10-30T082441.402.png"
    },
    {
      name: "Julia S.",
      role: "Marketing Managerin, Köln",
      text: "Die Brand-Kit Funktion ist genial! Einmal Logo und Farben hinterlegt, und alle Ads sind im Corporate Design. Spart enorm viel Zeit.",
      rating: 5,
      result: "Zeitersparnis: 15h/Woche",
      image: "https://customer-assets.emergentagent.com/job_minimal-adcraft/artifacts/b44ugssi_Screenshot%20-%202025-10-30T082600.134.png"
    },
    {
      name: "Andreas W.",
      role: "Selbstständiger Makler, Frankfurt",
      text: "Ich war skeptisch, ob KI wirklich gute Versicherungs-Ads erstellen kann. Aber die Ergebnisse überzeugen! Meine Kampagnen performen besser als je zuvor.",
      rating: 5,
      result: "ROI +180%",
      image: "https://customer-assets.emergentagent.com/job_minimal-adcraft/artifacts/5ue0zys8_Screenshot%20-%202025-10-30T082510.648.png"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      {/* Sticky Navigation */}
      <nav className="sticky top-0 z-50 bg-[#0B0E14]/95 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="/we-love-leads-logo.png" 
                alt="We Love Leads Logo" 
                className="h-8 w-auto"
              />
            </div>
            <button
              onClick={() => setShowAuth(true)}
              className="text-white hover:text-cyan-400 font-semibold transition-colors"
            >
              Anmelden
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section - DARK MODE POWER LOOK */}
      <div className="relative text-white py-24 overflow-hidden" style={{ backgroundColor: '#0B0E14' }}>
        {/* Animated Background Elements - Cyan/Purple Glow */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div 
            className="absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)' }}
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.4, 0.2] 
            }}
            transition={{ duration: 5, repeat: Infinity }}
          ></motion.div>
          <motion.div 
            className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)' }}
            animate={{ 
              scale: [1, 1.4, 1],
              opacity: [0.2, 0.5, 0.2] 
            }}
            transition={{ duration: 6, repeat: Infinity, delay: 1 }}
          ></motion.div>
          <motion.div 
            className="absolute top-1/3 left-1/4 w-72 h-72 rounded-full blur-3xl"
            style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)' }}
            animate={{ 
              scale: [1, 1.5, 1],
              x: [-30, 30, -30],
              y: [-20, 20, -20]
            }}
            transition={{ duration: 8, repeat: Infinity }}
          ></motion.div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            {/* Trust Element - Animated */}
            <motion.div 
              className="mb-4 flex items-center justify-center gap-2"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-2xl">⭐⭐⭐⭐⭐</span>
              <span className="text-sm font-semibold" style={{ color: '#F5F5F7' }}>
                4.8/5 von <CountUp end={200} duration={2.5} suffix="+" enableScrollSpy /> Finanzberatern
              </span>
            </motion.div>

            {/* Badge - Glassmorphism Style */}
            <motion.div 
              className="inline-block mb-6 px-6 py-3 rounded-full border shadow-xl backdrop-blur-xl"
              style={{ 
                backgroundColor: 'rgba(0, 242, 255, 0.1)',
                borderColor: 'rgba(0, 242, 255, 0.3)'
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="text-sm font-bold" style={{ color: '#00F2FF' }}>📊 BEWIESENE ERGEBNISSE FÜR FINANZDIENSTLEISTER</span>
            </motion.div>

            {/* Main Headline - MATHEMATISCHER BEWEIS VERSION */}
            <motion.h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ letterSpacing: '-0.02em', color: '#F5F5F7' }}
            >
              Von <span className="text-red-500 line-through opacity-70">38,13 €</span> auf <span style={{ color: '#00F2FF' }} className="font-black">4,31 €</span> pro Lead
              <br />
              <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl" style={{ color: 'rgba(245, 245, 247, 0.9)' }}>
                – ohne eine Sekunde Agentur-Support.
              </span>
            </motion.h1>

            {/* Subheadline - ROI FOCUSED */}
            <motion.p 
              className="text-lg sm:text-xl md:text-2xl mb-6 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              style={{ lineHeight: '1.6', color: 'rgba(245, 245, 247, 0.85)' }}
            >
              FinAds automatisiert das <span style={{ color: '#00F2FF' }} className="font-bold">Design</span>, das <span style={{ color: '#00F2FF' }} className="font-bold">Copywriting</span> und den <span style={{ color: '#00F2FF' }} className="font-bold">Meta-Launch</span>. Profi-Ergebnisse für Finanzdienstleister zum Bruchteil der üblichen Marketing-Kosten.
            </motion.p>
            
            {/* ===== CTA BUTTONS - DIREKT NACH HEADLINE FÜR MOBILE ===== */}
            <motion.div 
              className="mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {/* Primärer CTA - Kostenlos testen */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="mb-3"
              >
                <Button
                  onClick={() => navigate('/register')}
                  size="lg"
                  data-testid="hero-free-trial-btn"
                  className="relative text-white text-lg sm:text-xl px-12 sm:px-16 py-6 sm:py-8 font-bold shadow-2xl transition-all border-2 overflow-hidden group rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, #FF5C00 0%, #FF8A00 100%)',
                    borderColor: 'rgba(255, 92, 0, 0.5)',
                    boxShadow: '0 0 40px rgba(255, 92, 0, 0.4)'
                  }}
                >
                  {/* Animated shine effect */}
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                    animate={{ x: [-300, 300] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  ></motion.div>
                  
                  <div className="relative flex items-center gap-2 sm:gap-3">
                    <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
                    <span>Jetzt kostenlos testen</span>
                    <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Button>
              </motion.div>
              
              {/* Sekundärer CTA - Ersparnis berechnen */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                  variant="ghost"
                  size="lg"
                  data-testid="hero-pricing-btn"
                  className="text-white/70 hover:text-white text-base sm:text-lg px-8 sm:px-10 py-4 sm:py-5 font-medium transition-all border border-white/20 hover:border-white/40 bg-transparent hover:bg-white/5 rounded-full"
                >
                  <Calculator className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  <span>Ersparnis berechnen</span>
                  <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                </Button>
              </motion.div>
              
              {/* Trust-Zeile unter CTAs */}
              <motion.p 
                className="mt-4 text-xs sm:text-sm"
                style={{ color: 'rgba(245, 245, 247, 0.7)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <span style={{ color: '#00F2FF' }} className="font-semibold">✓ Keine Kreditkarte</span> • 
                <span style={{ color: '#00F2FF' }} className="font-semibold"> ✓ Keine Verpflichtung</span> • 
                <span style={{ color: '#00F2FF' }} className="font-semibold"> ✓ In 2 Min startklar</span>
              </motion.p>
            </motion.div>
            
            {/* ===== LIVE-DEMO INLINE IM HERO ===== */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mb-12 max-w-5xl mx-auto"
            >
              {/* Glowing Header */}
              <motion.div
                animate={{ 
                  boxShadow: ['0 0 30px rgba(249,115,22,0.4)', '0 0 60px rgba(249,115,22,0.7)', '0 0 30px rgba(249,115,22,0.4)']
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="inline-block bg-gradient-to-r from-orange-500 via-amber-400 to-orange-500 text-slate-900 text-sm font-black px-6 py-3 rounded-full mb-6 shadow-2xl"
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  LIVE-DEMO: Klicke & staune
                  <Sparkles className="w-5 h-5" />
                </span>
              </motion.div>
              
              {/* Compact Headline */}
              <p className="text-xl md:text-2xl text-white/80 mb-6">
                Wähle deine Sparte – sieh in <span className="text-orange-400 font-bold">5 Sekunden</span> dein Creative
              </p>

              {/* Sparten-Auswahl - SUPER AUFFÄLLIG */}
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                {Object.entries(spartenData).map(([key, data], index) => (
                  <motion.button
                    key={key}
                    onClick={() => handleSparteSelect(key)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 + index * 0.05 }}
                    className={`group relative px-5 py-3 rounded-2xl font-bold text-base transition-all duration-300 ${
                      selectedSparte === key 
                        ? 'bg-gradient-to-r ' + data.color + ' text-white shadow-2xl scale-110 ring-4 ring-white/40' 
                        : 'bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:scale-105 border-2 border-white/20 hover:border-orange-500/50'
                    }`}
                    whileHover={{ y: -3 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {/* Glow effect on hover */}
                    <motion.div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ boxShadow: '0 0 25px rgba(249,115,22,0.5)' }}
                    />
                    <span className="relative flex items-center gap-2">
                      <span className="text-xl">{data.icon}</span>
                      {data.name}
                    </span>
                    {selectedSparte === key && (
                      <motion.div
                        layoutId="activeDot"
                        className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rounded-full"
                      />
                    )}
                  </motion.button>
                ))}
              </div>

              {/* Generierungs-Animation */}
              {isGenerating && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-10"
                >
                  <div className="inline-block bg-slate-800/80 backdrop-blur-xl px-10 py-8 rounded-3xl border-2 border-orange-500/40 shadow-2xl">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="relative w-16 h-16">
                        <motion.div
                          className="absolute inset-0 rounded-full border-4 border-orange-500/30"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        />
                        <motion.div
                          className="absolute inset-2 rounded-full border-4 border-amber-400 border-t-transparent"
                          animate={{ rotate: -360 }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                        />
                        <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-orange-400" />
                      </div>
                      <div className="text-left">
                        <p className="text-xl font-bold text-white">KI generiert für {spartenData[selectedSparte]?.name}...</p>
                        <p className="text-orange-400/80">Creative • Headline • Benefits • CTA</p>
                      </div>
                    </div>
                    <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-orange-500 via-amber-400 to-orange-500 rounded-full"
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 1.8, ease: "easeInOut" }}
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Ergebnis - Großes zentriertes Smartphone-Mockup mit integrierter Ad-Copy */}
              {showResult && selectedSparte && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col items-center"
                >
                  {/* Smartphone Mockup - Groß & Zentriert */}
                  <div className="relative mb-8">
                    {/* Glow Effect */}
                    <div className="absolute -inset-6 bg-gradient-to-r from-orange-500/30 via-amber-500/30 to-orange-500/30 rounded-[4rem] blur-3xl" />
                    
                    {/* Phone Frame - Größer */}
                    <div className="relative w-80 md:w-96 bg-slate-900 rounded-[3rem] p-3 shadow-2xl border-4 border-slate-700">
                      {/* Screen */}
                      <div className={`w-full rounded-[2.5rem] bg-gradient-to-br ${spartenData[selectedSparte].color} p-3 overflow-hidden`}>
                        {/* Ad Content mit integrierter Copy */}
                        <div className="bg-white rounded-2xl p-4">
                          {/* Sponsored Tag */}
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-gray-800">Dein Unternehmen</p>
                              <p className="text-xs text-gray-500">Gesponsert</p>
                            </div>
                          </div>
                          
                          {/* Ad Image - Quadrat, vollständig */}
                          <div className="w-full rounded-xl overflow-hidden shadow-lg mb-3">
                            <img 
                              src={spartenData[selectedSparte].image} 
                              alt={spartenData[selectedSparte].name}
                              className="w-full h-auto"
                              style={{ aspectRatio: '1/1', objectFit: 'contain' }}
                            />
                          </div>
                          
                          {/* Ad Copy - Integriert im Mockup */}
                          <div className="space-y-2">
                            {/* Headline */}
                            <h3 className="text-lg font-black text-gray-900 leading-tight">
                              {spartenData[selectedSparte].headline} <span className="text-orange-600">{spartenData[selectedSparte].subheadline}</span>
                            </h3>
                            
                            {/* Body */}
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {spartenData[selectedSparte].body}
                            </p>
                            
                            {/* Benefits */}
                            <div className="space-y-1 py-2">
                              {spartenData[selectedSparte].benefits.map((benefit, i) => (
                                <p key={i} className="text-xs text-gray-800 font-medium">{benefit}</p>
                              ))}
                            </div>
                          </div>
                          
                          {/* CTA Button im Mockup */}
                          <button className={`w-full mt-2 py-3 rounded-xl bg-gradient-to-r ${spartenData[selectedSparte].color} text-white font-bold text-sm shadow-lg`}>
                            {spartenData[selectedSparte].cta}
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Floating Badges */}
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                      className="absolute -right-4 top-10 bg-emerald-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl"
                    >
                      ✓ KI-generiert
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                      className="absolute -left-4 bottom-24 bg-orange-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl"
                    >
                      ⚡ In 5 Sek erstellt
                    </motion.div>
                  </div>
                  
                  {/* CTA unter dem Mockup */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-center max-w-md"
                  >
                    <p className="text-white/70 text-lg mb-4">
                      Das ist nur ein <span className="text-orange-400 font-semibold">Vorgeschmack</span>...
                    </p>
                    <Button
                      onClick={() => navigate('/register')}
                      size="lg"
                      className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold px-10 py-6 text-lg shadow-xl shadow-orange-500/30 rounded-full"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      Alle 22 Sparten freischalten
                    </Button>
                  </motion.div>
                </motion.div>
              )}

              {/* Initial State */}
              {!selectedSparte && !isGenerating && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                  className="py-8"
                >
                  <motion.div 
                    className="inline-block bg-white/5 backdrop-blur-xl rounded-2xl p-8 border-2 border-dashed border-white/30"
                    animate={{ borderColor: ['rgba(255,255,255,0.3)', 'rgba(249,115,22,0.5)', 'rgba(255,255,255,0.3)'] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                      <ImageIcon className="w-16 h-16 text-orange-400/50 mx-auto mb-4" />
                    </motion.div>
                    <p className="text-white/70 text-lg font-medium">👆 Wähle eine Sparte und erlebe die Magie!</p>
                  </motion.div>
                </motion.div>
              )}
            </motion.div>
            
            {/* ===== AB HIER: WEITERE HERO-ELEMENTE (nach den CTAs) ===== */}
            
            {/* Kurze Value Prop */}
            <motion.p 
              className="text-sm sm:text-base mb-4 max-w-2xl mx-auto hidden sm:block"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              style={{ color: 'rgba(245, 245, 247, 0.7)' }}
            >
              Nutze die erste KI, die <span className="font-bold text-white">speziell für Finanzdienstleister</span> trainiert wurde und <span style={{ color: '#00F2FF' }} className="font-bold">direkt mit Meta synchronisiert</span>.
            </motion.p>
            
            {/* No-Brainer Element */}
            <motion.p 
              className="text-base mb-6 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              style={{ color: 'rgba(0, 242, 255, 0.9)' }}
            >
              ✨ Inklusive <strong>15+ fertigen Sparten-Vorlagen</strong> von PKV bis Hunde-OP – sofort einsatzbereit.
            </motion.p>

            {/* Social Proof - Glassmorphism Style */}
            <motion.div 
              className="mb-8 inline-block px-8 py-4 backdrop-blur-xl rounded-2xl border shadow-2xl"
              style={{
                backgroundColor: 'rgba(0, 242, 255, 0.08)',
                borderColor: 'rgba(0, 242, 255, 0.2)'
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              whileHover={{ scale: 1.05 }}
            >
              <p className="text-base md:text-xl font-bold flex items-center gap-2" style={{ color: '#F5F5F7' }}>
                <CheckCircle2 style={{ color: '#00F2FF' }} className="w-6 h-6" />
                "Endlich keine Angst mehr vor der nächsten Agentur-Rechnung!" – Sabine T., spart <CountUp end={1421} duration={2} suffix="€" enableScrollSpy />/Monat
              </p>
            </motion.div>

            {/* Value Proposition */}
            <motion.p 
              className="text-lg md:text-xl mb-6 font-semibold"
              style={{ color: 'rgba(245, 245, 247, 0.7)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              Speziell für Versicherungen & Finanzprodukte trainiert. Keine Design-Skills nötig.
            </motion.p>

            {/* APP SCREENSHOTS - Glassmorphism Cards */}
            <motion.div 
              className="mb-16 max-w-6xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3 }}
            >
              <div className="grid md:grid-cols-3 gap-6">
                {/* Screenshot 1: AdCopy Generator */}
                <div 
                  className="backdrop-blur-xl rounded-2xl p-4 border transition-all group hover:scale-105"
                  style={{ 
                    backgroundColor: 'rgba(0, 242, 255, 0.05)',
                    borderColor: 'rgba(0, 242, 255, 0.2)'
                  }}
                >
                  <div className="mb-4 rounded-xl overflow-hidden border-2 transition-all" style={{ borderColor: 'rgba(0, 242, 255, 0.2)' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_adcraft-finance/artifacts/dnr50k22_Screenshot%20-%202025-10-23T125603.516.png" 
                      alt="KI-AdCopy Generator"
                      className="w-full h-auto"
                    />
                  </div>
                  <h4 className="font-bold text-lg mb-2" style={{ color: '#00F2FF' }}>📝 KI schreibt für dich</h4>
                  <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                    Überzeugende Ad-Texte in 30 Sekunden – speziell für Versicherungen trainiert
                  </p>
                </div>

                {/* Screenshot 2: Image Generator */}
                <div 
                  className="backdrop-blur-xl rounded-2xl p-4 border transition-all group hover:scale-105"
                  style={{ 
                    backgroundColor: 'rgba(139, 92, 246, 0.05)',
                    borderColor: 'rgba(139, 92, 246, 0.2)'
                  }}
                >
                  <div className="mb-4 rounded-xl overflow-hidden border-2 transition-all" style={{ borderColor: 'rgba(139, 92, 246, 0.2)' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_adcraft-finance/artifacts/30we3alg_Screenshot%20-%202025-10-23T125440.810.png" 
                      alt="KI-Bildgenerierung"
                      className="w-full h-auto"
                    />
                  </div>
                  <h4 className="font-bold text-lg mb-2" style={{ color: '#A78BFA' }}>🎨 Professionelle Bilder</h4>
                  <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                    Unbegrenzt lizenzfreie Bilder erstellen – kein Designer nötig
                  </p>
                </div>

                {/* Screenshot 3: Templates */}
                <div 
                  className="backdrop-blur-xl rounded-2xl p-4 border transition-all group hover:scale-105"
                  style={{ 
                    backgroundColor: 'rgba(255, 92, 0, 0.05)',
                    borderColor: 'rgba(255, 92, 0, 0.2)'
                  }}
                >
                  <div className="mb-4 rounded-xl overflow-hidden border-2 transition-all" style={{ borderColor: 'rgba(255, 92, 0, 0.2)' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_financeads-ai/artifacts/xlxcyfwl_Screenshot%20-%202025-10-25T132823.082.png" 
                      alt="22 fertige Templates"
                      className="w-full h-auto"
                    />
                  </div>
                  <h4 className="font-bold text-lg mb-2" style={{ color: '#FF5C00' }}>🚀 22 bewährte Templates</h4>
                  <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                    Von BU bis Hausrat – einfach auswählen, Logo hochladen, fertig
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Company Logos Slider - Trust Bar */}
            <div className="mb-8">
              <p className="text-center text-sm font-medium mb-6 tracking-wide" style={{ color: 'rgba(0, 242, 255, 0.8)' }}>
                ✓ Vertraut von <strong style={{ color: '#00F2FF' }}>unabhängigen Vermittlern & Maklern</strong> dieser Unternehmen
              </p>
              
              <div className="relative overflow-hidden py-4">
                {/* Gradient overlays for smooth edges */}
                <div className="absolute left-0 top-0 bottom-0 w-24 z-10" style={{ background: 'linear-gradient(to right, #0B0E14, transparent)' }}></div>
                <div className="absolute right-0 top-0 bottom-0 w-24 z-10" style={{ background: 'linear-gradient(to left, #0B0E14, transparent)' }}></div>
                
                {/* Scrolling container */}
                <div className="flex animate-scroll-left">
                  {/* First set of logos */}
                  <div className="flex items-center gap-6 px-8 flex-shrink-0">
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/tecis.jpg" alt="Tecis" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/ovb.png" alt="OVB" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/telis.png" alt="Telis Finanz" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/signal-iduna.png" alt="Signal Iduna" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/barmenia.png" alt="Barmenia Gothaer" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/huk-coburg.png" alt="HUK Coburg" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/devk.png" alt="DEVK" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/ergo.png" alt="ERGO" className="h-8 w-auto" /></div>
                  </div>
                  {/* Second set for seamless loop */}
                  <div className="flex items-center gap-6 px-8 flex-shrink-0">
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/tecis.jpg" alt="Tecis" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/ovb.png" alt="OVB" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/telis.png" alt="Telis Finanz" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/signal-iduna.png" alt="Signal Iduna" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/barmenia.png" alt="Barmenia Gothaer" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/huk-coburg.png" alt="HUK Coburg" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/devk.png" alt="DEVK" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/ergo.png" alt="ERGO" className="h-8 w-auto" /></div>
                  </div>
                  {/* Third set for seamless loop */}
                  <div className="flex items-center gap-6 px-8 flex-shrink-0">
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/tecis.jpg" alt="Tecis" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/ovb.png" alt="OVB" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/telis.png" alt="Telis Finanz" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/signal-iduna.png" alt="Signal Iduna" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/barmenia.png" alt="Barmenia Gothaer" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/huk-coburg.png" alt="HUK Coburg" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/devk.png" alt="DEVK" className="h-8 w-auto" /></div>
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-300"><img src="/logos/ergo.png" alt="ERGO" className="h-8 w-auto" /></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Bar - After logos */}
            <div className="mb-8 bg-white/5 backdrop-blur-sm rounded-2xl border border-amber-500/20 py-6 px-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl md:text-3xl font-bold text-amber-400 mb-1">200+</div>
                  <div className="text-xs md:text-sm text-gray-300">Zufriedene Nutzer</div>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-bold text-amber-400 mb-1">22</div>
                  <div className="text-xs md:text-sm text-gray-300">Fertige Templates</div>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-bold text-amber-400 mb-1">95%</div>
                  <div className="text-xs md:text-sm text-gray-300">Kostenersparnis</div>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-bold text-amber-400 mb-1">5 Min</div>
                  <div className="text-xs md:text-sm text-gray-300">Erste Ad erstellt</div>
                </div>
              </div>
            </div>

            {/* Trust Badges - Enhanced */}
            <motion.div 
              className="flex flex-wrap justify-center gap-4 text-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.7 }}
            >
              <motion.div 
                className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border-2 border-white/20 shadow-lg"
                whileHover={{ scale: 1.05, borderColor: 'rgba(255,255,255,0.4)' }}
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                <span className="text-white font-semibold">Kostenlos testen • Keine Kreditkarte</span>
              </motion.div>
              <motion.div 
                className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border-2 border-white/20 shadow-lg"
                whileHover={{ scale: 1.05, borderColor: 'rgba(255,255,255,0.4)' }}
              >
                <Shield className="w-5 h-5 text-blue-300" />
                <span className="text-white font-semibold">Von Kollegen entwickelt</span>
              </motion.div>
              <motion.div 
                className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border-2 border-white/20 shadow-lg"
                whileHover={{ scale: 1.05, borderColor: 'rgba(255,255,255,0.4)' }}
              >
                <Star className="w-5 h-5 text-yellow-300" />
                <span className="text-white font-semibold">Jederzeit kündbar</span>
              </motion.div>
              <motion.div 
                className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border-2 border-white/20 shadow-lg"
                whileHover={{ scale: 1.05, borderColor: 'rgba(255,255,255,0.4)' }}
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                <span className="text-white font-semibold">DSGVO-konform & Made in Germany</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ===== ELITE AUTOMATION MODULE - Agency Killer ===== */}
      <div className="py-20 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block bg-gradient-to-r from-orange-500 to-yellow-500 text-slate-900 text-sm font-bold px-6 py-2 rounded-full mb-6 shadow-lg">
              🚀 ELITE AUTOMATION
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Vom kreativen Funken zum Live-Lead<br/>
              <span className="bg-gradient-to-r from-yellow-300 via-orange-300 to-yellow-300 bg-clip-text text-transparent">in 60 Sekunden.</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Dein gesamtes Marketing-Team in einem Dashboard. Erstellen, Planen und Schalten – 
              <strong className="text-yellow-300"> ohne jemals den Meta Werbe-Manager öffnen zu müssen.</strong>
            </p>
          </motion.div>

          {/* 3-Phase Ecosystem */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="grid md:grid-cols-3 gap-6 mb-16"
          >
            {/* Phase 1: Generate */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all" />
              <div className="relative bg-slate-800/80 backdrop-blur-sm border border-yellow-500/30 rounded-2xl p-8 h-full hover:border-yellow-400/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-yellow-500/30">
                  <Sparkles className="w-8 h-8 text-slate-900" />
                </div>
                <div className="text-yellow-400 text-sm font-bold mb-2">PHASE 1</div>
                <h3 className="text-2xl font-bold text-white mb-3">GENERIEREN</h3>
                <p className="text-white/70">
                  Deine KI erstellt psychologisch optimierte Bilder und Texte für PKV, BU, Immobilien oder Altersvorsorge.
                </p>
              </div>
            </div>

            {/* Phase 2: Target */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all" />
              <div className="relative bg-slate-800/80 backdrop-blur-sm border border-orange-500/30 rounded-2xl p-8 h-full hover:border-orange-400/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-orange-500/30">
                  <Target className="w-8 h-8 text-slate-900" />
                </div>
                <div className="text-orange-400 text-sm font-bold mb-2">PHASE 2</div>
                <h3 className="text-2xl font-bold text-white mb-3">TARGETIEREN</h3>
                <p className="text-white/70">
                  Das System wählt automatisch die Zielgruppen-Struktur aus, die bei Makler-Kollegen bereits nachweislich funktioniert.
                </p>
              </div>
            </div>

            {/* Phase 3: Launch */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all" />
              <div className="relative bg-slate-800/80 backdrop-blur-sm border border-emerald-500/30 rounded-2xl p-8 h-full hover:border-emerald-400/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-green-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/30">
                  <Zap className="w-8 h-8 text-slate-900" />
                </div>
                <div className="text-emerald-400 text-sm font-bold mb-2">PHASE 3</div>
                <h3 className="text-2xl font-bold text-white mb-3">LAUNCHEN</h3>
                <p className="text-white/70">
                  Ein Klick auf „Veröffentlichen". Unsere Schnittstelle baut die Kampagne fehlerfrei in deinem Meta-Konto auf.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Ownership vs Rent - Financial Logic */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="bg-slate-800/50 border border-white/10 rounded-2xl p-8 mb-16"
          >
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-2">Warum monatlich 1.500 € Agentur-Gebühren zahlen?</h3>
              <p className="text-white/70 max-w-2xl mx-auto">
                Mit dem Elite-Upgrade kaufst du dir deine eigene Marketing-Infrastruktur. 
                Einmalig investieren, lebenslang Leads generieren.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Agency Cost */}
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                <div className="text-red-400 text-sm font-bold mb-2">❌ AGENTUR (MIETE)</div>
                <div className="text-4xl font-bold text-red-400 mb-2">1.500 €<span className="text-lg">/Monat</span></div>
                <div className="text-red-300/70">= 18.000 € pro Jahr</div>
                <ul className="mt-4 space-y-2 text-white/60 text-sm">
                  <li>• Monatliche Dauerbelastung</li>
                  <li>• Abhängigkeit von Dritten</li>
                  <li>• Keine Kontrolle über Kampagnen</li>
                </ul>
              </div>
              {/* Elite Cost */}
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6">
                <div className="text-emerald-400 text-sm font-bold mb-2">✅ ELITE AUTOMATION (BESITZ)</div>
                <div className="text-4xl font-bold text-emerald-400 mb-2">799 €<span className="text-lg"> einmalig</span></div>
                <div className="text-emerald-300/70">= Lebenslanger Zugang</div>
                <ul className="mt-4 space-y-2 text-white/60 text-sm">
                  <li>• Einmalige Investition</li>
                  <li>• Volle Kontrolle</li>
                  <li>• Amortisiert sich mit 1. PKV-Abschluss</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Agency-Killer Feature Stack */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mb-16"
          >
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              Das bekommst du mit <span className="text-yellow-400">Elite Automation</span>:
            </h3>
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {[
                { icon: Zap, text: 'Vollautomatische Meta-Schnittstelle: Keine Technik-Hölle, kein Copy-Paste. Direkter Kampagnen-Export.' },
                { icon: Target, text: 'Exklusive Targeting-Blueprints: Zugriff auf die Interessen-Sets der Top-Performer.' },
                { icon: TrendingUp, text: 'Zentrale Lead-Übersicht: Behalte die Kontrolle über deine Performance direkt im Dashboard.' },
                { icon: Shield, text: 'Priority API-Wartung: Wir halten dein System immer auf dem neuesten Stand der Meta-Algorithmen.' },
                { icon: Clock, text: 'Kampagnen in 60 Sekunden: Statt 30 Minuten im Werbeanzeigenmanager.' },
                { icon: CheckCircle2, text: 'Lead-Formulare automatisch: Kein manuelles Setup mehr nötig.' }
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * i }}
                  className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-4 hover:border-yellow-500/30 transition-all"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-slate-900" />
                  </div>
                  <p className="text-white/80 text-sm">{feature.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Pricing Comparison - Toggle Version */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="max-w-5xl mx-auto"
            id="pricing"
          >
            {/* Toggle Switch - Monatlich / Lifetime */}
            <div className="flex justify-center mb-10">
              <div className="bg-slate-800/80 backdrop-blur-md p-1.5 rounded-full border border-white/20 inline-flex items-center">
                <button
                  onClick={() => setPricingMode('monthly')}
                  className={`px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${
                    pricingMode === 'monthly'
                      ? 'bg-blue-500 text-white shadow-lg'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  Monatliche Flexibilität
                </button>
                <button
                  onClick={() => setPricingMode('lifetime')}
                  className={`px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 relative ${
                    pricingMode === 'lifetime'
                      ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-slate-900 shadow-lg'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  Einmalig zahlen (Lifetime)
                  {pricingMode !== 'lifetime' && (
                    <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full">
                      SPAR-TIPP
                    </span>
                  )}
                </button>
              </div>
            </div>
            
            {/* Savings Hint for Lifetime */}
            {pricingMode === 'lifetime' && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center text-emerald-400 text-sm mb-8 font-semibold"
              >
                ✨ Beliebteste Wahl – Spare über 70% gegenüber dem Abo!
              </motion.p>
            )}

            {/* Two Package Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* STARTER Package */}
              <motion.div
                key={`starter-${pricingMode}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-slate-800/50 border border-white/20 rounded-2xl p-8 relative"
              >
                <div className="text-white/60 text-sm font-bold mb-3 tracking-wider">STARTER</div>
                <div className="text-white/50 text-sm mb-4">Für Einsteiger & Tester</div>
                
                {/* Price based on mode */}
                <div className="mb-6">
                  {pricingMode === 'monthly' ? (
                    <>
                      <div className="text-4xl font-bold text-white mb-1">
                        {includeTargeting ? '68' : '19'} €<span className="text-lg text-white/60">/Monat</span>
                      </div>
                      <p className="text-white/50 text-sm">Monatlich kündbar</p>
                    </>
                  ) : (
                    <>
                      <div className="text-4xl font-bold text-white mb-1">
                        {includeTargeting ? '138,99' : '79,99'} €<span className="text-lg text-white/60"> einmalig</span>
                      </div>
                      <p className="text-emerald-400 text-sm">Einmal zahlen, für immer nutzen</p>
                    </>
                  )}
                </div>
                
                {/* Features */}
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span>Unlimitierte KI-Anzeigen</span>
                  </li>
                  <li className="flex items-center gap-3 text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span>Targeting als Vorlage</span>
                  </li>
                  <li className="flex items-center gap-3 text-white/50">
                    <span className="w-5 h-5 text-center text-sm">❌</span>
                    <span>Meta-Anbindung (manueller Export)</span>
                  </li>
                  <li className="flex items-center gap-3 text-white/50">
                    <Clock className="w-5 h-5 flex-shrink-0" />
                    <span>Zeitaufwand: ca. 15 Min. pro Ad</span>
                  </li>
                </ul>
                
                <Button
                  onClick={() => handleCheckout(pricingMode === 'monthly' ? 'monthly' : 'lifetime')}
                  disabled={checkoutLoading}
                  className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/30 py-5 font-semibold"
                >
                  {checkoutLoading ? 'Lädt...' : 'Starter wählen'}
                </Button>
              </motion.div>

              {/* ELITE Package - Highlighted */}
              <motion.div
                key={`elite-${pricingMode}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="relative"
              >
                {/* Bestseller Badge */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-slate-900 text-sm font-bold px-5 py-2 rounded-full shadow-lg whitespace-nowrap">
                    ⭐ AGENCY-KILLER
                  </span>
                </div>
                
                <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/50 rounded-2xl p-8 relative overflow-hidden h-full">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-orange-500/5 animate-pulse" />
                  
                  <div className="relative z-10">
                    <div className="text-yellow-400 text-sm font-bold mb-3 tracking-wider">ELITE</div>
                    <div className="text-white/70 text-sm mb-4">Der „Agency-Killer" für Profis</div>
                    
                    {/* Price based on mode */}
                    <div className="mb-6">
                      {pricingMode === 'monthly' ? (
                        <>
                          <div className="text-4xl font-bold text-white mb-1">
                            {includeTargeting ? '348' : '299'} €<span className="text-lg text-white/60">/Monat</span>
                          </div>
                          <p className="text-white/50 text-sm">Monatlich kündbar</p>
                        </>
                      ) : (
                        <>
                          <div className="text-4xl font-bold text-white mb-1">
                            {includeTargeting ? '858' : '799'} €<span className="text-lg text-white/60"> einmalig</span>
                          </div>
                          <p className="text-emerald-400 text-sm">
                            Nach {pricingMode === 'monthly' ? '' : '2,6 Monaten amortisiert vs. Flex'}
                          </p>
                        </>
                      )}
                    </div>
                    
                    {/* Features */}
                    <ul className="space-y-3 mb-8">
                      <li className="flex items-center gap-3 text-white">
                        <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                        <span>Unlimitierte KI-Anzeigen</span>
                      </li>
                      <li className="flex items-center gap-3 text-white">
                        <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                        <span>Profi-Targeting vollautomatisch</span>
                      </li>
                      <li className="flex items-center gap-3 text-white">
                        <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                        <span className="font-semibold">Meta-Anbindung: 1-Klick Launch</span>
                      </li>
                      <li className="flex items-center gap-3 text-white">
                        <Zap className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                        <span className="font-semibold">Zeitaufwand: Unter 60 Sekunden</span>
                      </li>
                      <li className="flex items-center gap-3 text-white">
                        <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                        <span>Lead-Formulare automatisch erstellt</span>
                      </li>
                    </ul>
                    
                    <Button
                      onClick={() => handleCheckout(pricingMode === 'monthly' ? 'elite_monthly' : 'elite')}
                      disabled={checkoutLoading}
                      className="w-full bg-gradient-to-r from-yellow-400 via-orange-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-slate-900 font-bold py-5 shadow-xl shadow-yellow-500/30"
                    >
                      <Zap className="w-5 h-5 mr-2" />
                      {checkoutLoading ? 'Lädt...' : 'Elite freischalten'}
                    </Button>
                    
                    <p className="text-center text-white/60 text-xs mt-4">
                      Ersetzt eine 18.000 €/Jahr Agentur.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Targeting Helper Add-on Toggle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-xl p-6 max-w-2xl mx-auto"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-lg">+ Targeting Helper</h4>
                    <p className="text-white/60 text-sm">Exklusive Interessen-Sets der Top-Performer</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-purple-400 font-bold">
                    +{pricingMode === 'monthly' ? '49€/Mo' : '59€'}
                  </span>
                  <button
                    onClick={() => setIncludeTargeting(!includeTargeting)}
                    className={`w-14 h-8 rounded-full transition-all duration-300 relative ${
                      includeTargeting 
                        ? 'bg-gradient-to-r from-purple-500 to-blue-500' 
                        : 'bg-slate-600'
                    }`}
                  >
                    <div className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-all duration-300 shadow-md ${
                      includeTargeting ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </div>
              </div>
              {includeTargeting && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-4 pt-4 border-t border-purple-500/20"
                >
                  <ul className="grid grid-cols-2 gap-2 text-sm text-white/70">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-400" />
                      BU Interessen-Set
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-400" />
                      PKV Interessen-Set
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-400" />
                      Altersvorsorge-Set
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-400" />
                      Immobilien-Set
                    </li>
                  </ul>
                </motion.div>
              )}
            </motion.div>
          </motion.div>

          {/* Knaller-Satz */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-white/70 mt-8 max-w-3xl mx-auto"
          >
            {pricingMode === 'monthly' ? (
              <>Teste flexibel und wechsle jederzeit auf Lifetime, wenn du überzeugt bist.</>
            ) : (
              <>Die meisten Profis wählen die Einmalzahlung – sie hat sich oft schon mit dem ersten PKV- oder Immobilien-Abschluss refinanziert.</>
            )}
          </motion.p>
        </div>
      </div>

      {/* ===== THE AGENCY-KILLER: Schmerz-Sektion ===== */}
      <div className="py-20" style={{ backgroundColor: '#0B0E14' }}>
        <div className="max-w-5xl mx-auto px-6">
          {/* Pain-Point Headline */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span 
              className="inline-block px-4 py-2 rounded-full text-sm font-bold mb-6"
              style={{ backgroundColor: 'rgba(255, 92, 0, 0.15)', color: '#FF5C00', border: '1px solid rgba(255, 92, 0, 0.3)' }}
            >
              🔥 DER AGENCY-KILLER
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold mb-6" style={{ color: '#F5F5F7' }}>
              Warum <span style={{ color: '#FF5C00' }}>1.500 € pro Monat</span> für Agenturen verbrennen,<br className="hidden md:block" /> die dein Geschäft gar nicht verstehen?
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
              Schluss mit der <strong style={{ color: '#FF5C00' }}>„Blackbox" Marketing</strong>. Ersetze teure Retainer, endlose Korrekturschleifen und schlechte Leads durch die erste KI, die speziell für <strong style={{ color: '#00F2FF' }}>Versicherungsprofis</strong> trainiert wurde.
            </p>
          </motion.div>

          {/* Enhanced Comparison - MOBILE OPTIMIZED */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="backdrop-blur-xl rounded-2xl border overflow-hidden shadow-2xl"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', borderColor: 'rgba(0, 242, 255, 0.3)' }}
          >
            {/* Desktop Table - Hidden on Mobile */}
            <table className="w-full hidden md:table">
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(0, 242, 255, 0.3)' }}>
                  <th className="py-5 px-6 text-left text-sm font-semibold" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>Was du bekommst</th>
                  <th className="py-5 px-6 text-center" style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)' }}>
                    <span className="text-red-400 font-bold text-lg">❌ Marketing-Agentur</span>
                    <span className="block text-red-300/60 text-xs mt-1">Abhängigkeit & Kontrollverlust</span>
                  </th>
                  <th className="py-5 px-6 text-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)' }}>
                    <span style={{ color: '#00F2FF' }} className="font-bold text-lg">✅ FinAds Creator (KI)</span>
                    <span className="block text-cyan-300/60 text-xs mt-1">100% Kontrolle & Unabhängigkeit</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <td className="py-5 px-6 font-semibold" style={{ color: '#F5F5F7' }}>💰 Kosten</td>
                  <td className="py-5 px-6 text-center font-bold text-red-400" style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)' }}>
                    <span className="text-xl line-through">1.500 € – 3.000 €</span>
                    <span className="block text-sm text-red-300/70 mt-1">„Agentur-Rente" – jeden Monat</span>
                  </td>
                  <td className="py-5 px-6 text-center font-bold" style={{ backgroundColor: 'rgba(0, 242, 255, 0.08)', color: '#00F2FF' }}>
                    <span className="text-2xl">79,99 €</span>
                    <span className="block text-sm text-cyan-300/80 mt-1">Einmalig = Lebenslanger Profit</span>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <td className="py-5 px-6 font-semibold" style={{ color: '#F5F5F7' }}>⏱️ Speed</td>
                  <td className="py-5 px-6 text-center text-red-400" style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)' }}>
                    <span className="font-semibold">2–4 Wochen Wartezeit</span>
                    <span className="block text-sm text-red-300/70 mt-1">Lange Feedbackschleifen</span>
                  </td>
                  <td className="py-5 px-6 text-center font-semibold" style={{ backgroundColor: 'rgba(0, 242, 255, 0.08)', color: '#00F2FF' }}>
                    <span className="text-xl">⚡ Sofort-Start</span>
                    <span className="block text-sm text-cyan-300/80 mt-1">In unter 60 Sekunden live</span>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <td className="py-5 px-6 font-semibold" style={{ color: '#F5F5F7' }}>🎯 Branchenwissen</td>
                  <td className="py-5 px-6 text-center text-red-400" style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)' }}>
                    <span className="font-semibold">„Wir machen alles"-Mentalität</span>
                    <span className="block text-sm text-red-300/70 mt-1">Kennt PKV vs. GKV nicht</span>
                  </td>
                  <td className="py-5 px-6 text-center font-semibold" style={{ backgroundColor: 'rgba(0, 242, 255, 0.08)', color: '#00F2FF' }}>
                    <span>Spezial-KI für Versicherungen</span>
                    <span className="block text-sm text-cyan-300/80 mt-1">15+ Sparten deep-trained</span>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <td className="py-5 px-6 font-semibold" style={{ color: '#F5F5F7' }}>📊 Kontrolle</td>
                  <td className="py-5 px-6 text-center text-red-400" style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)' }}>
                    <span className="font-semibold">„Blackbox" Marketing</span>
                    <span className="block text-sm text-red-300/70 mt-1">Abhängig vom Account-Manager</span>
                  </td>
                  <td className="py-5 px-6 text-center font-semibold" style={{ backgroundColor: 'rgba(0, 242, 255, 0.08)', color: '#00F2FF' }}>
                    <span>100 % Transparenz</span>
                    <span className="block text-sm text-cyan-300/80 mt-1">Du bestimmst alles selbst</span>
                  </td>
                </tr>
                <tr>
                  <td className="py-5 px-6 font-semibold" style={{ color: '#F5F5F7' }}>🔄 Anpassungen</td>
                  <td className="py-5 px-6 text-center text-red-400" style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)' }}>
                    <span className="font-semibold">Kosten extra Geld</span>
                    <span className="block text-sm text-red-300/70 mt-1">Endlose Korrekturschleifen</span>
                  </td>
                  <td className="py-5 px-6 text-center font-semibold" style={{ backgroundColor: 'rgba(0, 242, 255, 0.08)', color: '#00F2FF' }}>
                    <span>Unlimitiert & Echtzeit</span>
                    <span className="block text-sm text-cyan-300/80 mt-1">Ändern so oft du willst</span>
                  </td>
                </tr>
              </tbody>
            </table>
            
            {/* Mobile Card View - Hidden on Desktop */}
            <div className="md:hidden p-4 space-y-4">
              {/* Header Cards */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)' }}>
                  <span className="text-red-400 font-bold text-sm">❌ Agentur</span>
                </div>
                <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)' }}>
                  <span style={{ color: '#00F2FF' }} className="font-bold text-sm">✅ FinAds</span>
                </div>
              </div>
              
              {/* Comparison Row 1: Kosten */}
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="p-3 text-center font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#F5F5F7' }}>
                  💰 Kosten
                </div>
                <div className="grid grid-cols-2">
                  <div className="p-4 text-center" style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)' }}>
                    <span className="text-red-400 font-bold text-lg line-through block">1.500€+</span>
                    <span className="text-red-300/70 text-xs">/Monat</span>
                  </div>
                  <div className="p-4 text-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.08)' }}>
                    <span style={{ color: '#00F2FF' }} className="font-bold text-xl block">79,99€</span>
                    <span className="text-cyan-300/80 text-xs">einmalig</span>
                  </div>
                </div>
              </div>
              
              {/* Comparison Row 2: Speed */}
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="p-3 text-center font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#F5F5F7' }}>
                  ⏱️ Speed
                </div>
                <div className="grid grid-cols-2">
                  <div className="p-4 text-center" style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)' }}>
                    <span className="text-red-400 font-semibold text-sm block">2–4 Wochen</span>
                    <span className="text-red-300/70 text-xs">Wartezeit</span>
                  </div>
                  <div className="p-4 text-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.08)' }}>
                    <span style={{ color: '#00F2FF' }} className="font-bold text-sm block">⚡ Sofort</span>
                    <span className="text-cyan-300/80 text-xs">In 60 Sek. live</span>
                  </div>
                </div>
              </div>
              
              {/* Comparison Row 3: Branchenwissen */}
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="p-3 text-center font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#F5F5F7' }}>
                  🎯 Branchenwissen
                </div>
                <div className="grid grid-cols-2">
                  <div className="p-4 text-center" style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)' }}>
                    <span className="text-red-400 font-semibold text-sm block">Generalist</span>
                    <span className="text-red-300/70 text-xs">Keine Expertise</span>
                  </div>
                  <div className="p-4 text-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.08)' }}>
                    <span style={{ color: '#00F2FF' }} className="font-bold text-sm block">Spezialist</span>
                    <span className="text-cyan-300/80 text-xs">15+ Sparten</span>
                  </div>
                </div>
              </div>
              
              {/* Comparison Row 4: Kontrolle */}
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="p-3 text-center font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#F5F5F7' }}>
                  📊 Kontrolle
                </div>
                <div className="grid grid-cols-2">
                  <div className="p-4 text-center" style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)' }}>
                    <span className="text-red-400 font-semibold text-sm block">Blackbox</span>
                    <span className="text-red-300/70 text-xs">Abhängig</span>
                  </div>
                  <div className="p-4 text-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.08)' }}>
                    <span style={{ color: '#00F2FF' }} className="font-bold text-sm block">100% du</span>
                    <span className="text-cyan-300/80 text-xs">Volle Kontrolle</span>
                  </div>
                </div>
              </div>
              
              {/* Comparison Row 5: Anpassungen */}
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="p-3 text-center font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#F5F5F7' }}>
                  🔄 Anpassungen
                </div>
                <div className="grid grid-cols-2">
                  <div className="p-4 text-center" style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)' }}>
                    <span className="text-red-400 font-semibold text-sm block">Extra €€€</span>
                    <span className="text-red-300/70 text-xs">Jede Änderung</span>
                  </div>
                  <div className="p-4 text-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.08)' }}>
                    <span style={{ color: '#00F2FF' }} className="font-bold text-sm block">Unlimitiert</span>
                    <span className="text-cyan-300/80 text-xs">Echtzeit</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Savings Callout */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-10 p-6 rounded-xl"
            style={{ backgroundColor: 'rgba(0, 242, 255, 0.05)', border: '1px solid rgba(0, 242, 255, 0.2)' }}
          >
            <p className="text-xl" style={{ color: '#F5F5F7' }}>
              💡 <strong style={{ color: '#FF5C00' }}>Rechne selbst:</strong> Bei 1.500€/Monat Agenturkosten = <span className="line-through text-red-400">18.000€/Jahr</span>
            </p>
            <p className="text-2xl font-bold mt-2" style={{ color: '#00F2FF' }}>
              Mit FinAds sparst du <span style={{ color: '#00F2FF' }}>17.920,01 € im ersten Jahr</span>.
            </p>
          </motion.div>
        </div>
      </div>

      {/* ===== TEAM SEKTION: Von Maklern für Makler ===== */}
      <div className="py-16" style={{ backgroundColor: '#080A0F' }}>
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            {/* Team Image */}
            <div className="relative">
              <div 
                className="rounded-2xl overflow-hidden border-2 shadow-2xl"
                style={{ borderColor: 'rgba(0, 242, 255, 0.3)' }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_ad-optimizer-8/artifacts/j2bidtyc_unnamed%20%281%29.jpg"
                  alt="Das FinAds Team"
                  className="w-full h-auto"
                />
              </div>
              {/* Floating badge */}
              <div 
                className="absolute -bottom-4 -right-4 px-4 py-2 rounded-xl shadow-lg"
                style={{ backgroundColor: '#00F2FF', color: '#0B0E14' }}
              >
                <span className="font-bold text-sm">Aus der Branche</span>
              </div>
            </div>

            {/* Text Content */}
            <div>
              <span 
                className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4"
                style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)', color: '#00F2FF' }}
              >
                👋 DIE KÖPFE HINTER FINADS
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#F5F5F7' }}>
                Von Maklern <span style={{ color: '#00F2FF' }}>für Makler</span> entwickelt.
              </h2>
              <p className="text-lg mb-6" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                Wir kommen selbst aus der Finanzbranche und kennen den Frust: Teure Agenturen, die das Geschäft nicht verstehen. Endlose Feedback-Schleifen. Und am Ende Ads, die nicht performen.
              </p>
              <p className="text-lg mb-6" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                <strong style={{ color: '#FF5C00' }}>Deshalb haben wir FinAds gebaut</strong> – das Tool, das wir selbst immer gebraucht hätten. Speziell für Versicherungen und Finanzprodukte trainiert.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex -space-x-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold text-sm">MK</div>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white font-bold text-sm">JS</div>
                </div>
                <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
                  Marketing & Entwicklung – <strong style={{ color: '#00F2FF' }}>100% Finanzbranche</strong>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ===== DEEP TRUST: Echte Meta Ads-Daten ===== */}
      <div className="py-20" style={{ backgroundColor: '#080A0F' }}>
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span 
              className="inline-block px-4 py-2 rounded-full text-sm font-bold mb-6"
              style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)', color: '#00F2FF', border: '1px solid rgba(0, 242, 255, 0.3)' }}
            >
              📊 ECHTE ERGEBNISSE – KEINE FAKE-SCREENSHOTS
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold mb-6" style={{ color: '#F5F5F7' }}>
              <span style={{ color: '#FF5C00' }}>4,07€</span> pro Lead. <span style={{ color: '#00F2FF' }}>Verifizierte Daten.</span>
            </h2>
            <p className="text-xl max-w-3xl mx-auto mb-6" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
              Keine Versprechungen – <strong>echte Screenshots</strong> aus dem Meta Ads Manager unserer Kunden.
            </p>
            
            {/* VERIFIED Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-green-500/20 border border-green-400/40">
              <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-green-400 font-semibold text-sm">VERIFIZIERTE META ADS DATEN</span>
            </div>
          </motion.div>
          
          {/* Vorher/Nachher Vergleich - NEW */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-6 mb-12"
          >
            <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', border: '1px solid rgba(220, 38, 38, 0.3)' }}>
              <div className="text-red-400 font-bold text-lg mb-3">❌ VOR FinAds</div>
              <div className="space-y-2 text-white/80">
                <p>• <span className="line-through text-red-300">25€ – 85€</span> pro Lead</p>
                <p>• Agenturkosten: <span className="line-through text-red-300">1.500€/Monat</span></p>
                <p>• Wartezeit: <span className="text-red-300">2-4 Wochen</span> für neue Ads</p>
                <p>• <span className="text-red-300">Null Kontrolle</span> über Creatives</p>
              </div>
            </div>
            <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)', border: '1px solid rgba(0, 242, 255, 0.3)' }}>
              <div className="font-bold text-lg mb-3" style={{ color: '#00F2FF' }}>✅ MIT FinAds</div>
              <div className="space-y-2 text-white/80">
                <p>• <span className="font-bold text-green-400">4,07€ – 18€</span> pro Lead</p>
                <p>• Einmalzahlung: <span className="font-bold" style={{ color: '#00F2FF' }}>79,99€</span></p>
                <p>• Neue Ads in <span className="font-bold text-green-400">unter 60 Sekunden</span></p>
                <p>• <span className="font-bold" style={{ color: '#00F2FF' }}>100% Kontrolle</span> – du entscheidest</p>
              </div>
            </div>
          </motion.div>

          {/* Screenshots Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Screenshot 1 - 4,07€ pro Lead */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div 
                className="rounded-2xl overflow-hidden border-2 shadow-2xl transition-all duration-300 group-hover:scale-[1.02]"
                style={{ borderColor: 'rgba(0, 242, 255, 0.4)', backgroundColor: 'rgba(0, 242, 255, 0.05)' }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_dark-finance-6/artifacts/012iyg3a_Screenshot%20-%202026-01-19T095252.265.png"
                  alt="Meta Ads Manager - 4,07€ pro Lead"
                  className="w-full h-auto"
                />
              </div>
              {/* Highlight Badge */}
              <motion.div 
                className="absolute -top-4 -right-4 px-5 py-3 rounded-xl shadow-lg"
                style={{ backgroundColor: '#00F2FF', color: '#0B0E14' }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <span className="font-extrabold text-lg">4,07€/Lead</span>
              </motion.div>
              <div className="mt-4 text-center">
                <p className="text-lg font-semibold" style={{ color: '#F5F5F7' }}>
                  <span style={{ color: '#00F2FF' }}>607 Leads</span> generiert • 41.169 Reichweite
                </p>
                <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
                  Dynamische Bilder Kampagne – 7 Tage Laufzeit
                </p>
              </div>
            </motion.div>

            {/* Screenshot 2 - Kampagnen-Übersicht */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div 
                className="rounded-2xl overflow-hidden border-2 shadow-2xl transition-all duration-300 group-hover:scale-[1.02]"
                style={{ borderColor: 'rgba(255, 92, 0, 0.4)', backgroundColor: 'rgba(255, 92, 0, 0.05)' }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_dark-finance-6/artifacts/k3ax746q_Screenshot%20-%202026-01-19T095326.959.png"
                  alt="Meta Ads Manager - Kampagnen Übersicht"
                  className="w-full h-auto"
                />
              </div>
              {/* Highlight Badge */}
              <motion.div 
                className="absolute -top-4 -right-4 px-5 py-3 rounded-xl shadow-lg"
                style={{ backgroundColor: '#FF5C00', color: 'white' }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              >
                <span className="font-extrabold text-lg">Ø 18,35€/Lead</span>
              </motion.div>
              <div className="mt-4 text-center">
                <p className="text-lg font-semibold" style={{ color: '#F5F5F7' }}>
                  Mehrere <span style={{ color: '#FF5C00' }}>Kampagnen</span> gleichzeitig
                </p>
                <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
                  Verschiedene Sparten: BU, PKV, Zahnzusatz & mehr
                </p>
              </div>
            </motion.div>
          </div>

          {/* KPI Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            <div 
              className="text-center p-6 rounded-xl"
              style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)', border: '1px solid rgba(0, 242, 255, 0.2)' }}
            >
              <div className="text-4xl font-extrabold mb-2" style={{ color: '#00F2FF' }}>4,07€</div>
              <p className="text-sm font-medium" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>Bester CPL</p>
            </div>
            <div 
              className="text-center p-6 rounded-xl"
              style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)', border: '1px solid rgba(255, 92, 0, 0.2)' }}
            >
              <div className="text-4xl font-extrabold mb-2" style={{ color: '#FF5C00' }}>607</div>
              <p className="text-sm font-medium" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>Leads generiert</p>
            </div>
            <div 
              className="text-center p-6 rounded-xl"
              style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)', border: '1px solid rgba(0, 242, 255, 0.2)' }}
            >
              <div className="text-4xl font-extrabold mb-2" style={{ color: '#00F2FF' }}>4.4M+</div>
              <p className="text-sm font-medium" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>Impressionen</p>
            </div>
            <div 
              className="text-center p-6 rounded-xl"
              style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)', border: '1px solid rgba(255, 92, 0, 0.2)' }}
            >
              <div className="text-4xl font-extrabold mb-2" style={{ color: '#FF5C00' }}>79k€</div>
              <p className="text-sm font-medium" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>Ausgegeben</p>
            </div>
          </motion.div>

          {/* Trust Note */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-8 text-sm"
            style={{ color: 'rgba(245, 245, 247, 0.5)' }}
          >
            * Screenshots direkt aus dem Meta Ads Manager. Ergebnisse können variieren und hängen von der Zielgruppe, dem Budget und der Optimierung ab.
          </motion.p>
        </div>
      </div>

      {/* ===== ENTSPANNTER MAKLER SEKTION ===== */}
      <div className="py-20" style={{ backgroundColor: '#0B0E14' }}>
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            {/* Text Content - Left Side */}
            <div className="order-2 md:order-1">
              <span 
                className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4"
                style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)', color: '#FF5C00' }}
              >
                📱 SO FÜHLT SICH ERFOLG AN
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#F5F5F7' }}>
                Lehne dich zurück. <span style={{ color: '#00F2FF' }}>Die Leads kommen zu dir.</span>
              </h2>
              <p className="text-lg mb-6" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                Stell dir vor: Du sitzt entspannt im Büro, und dein Handy meldet <strong style={{ color: '#00F2FF' }}>"Ein neuer Lead"</strong>. Und noch einer. Und noch einer.
              </p>
              <p className="text-lg mb-6" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                Kein Kaltakquise-Stress. Keine nervigen Telefonate. Einfach <strong style={{ color: '#FF5C00' }}>qualifizierte Anfragen</strong>, die zu dir kommen – dank Ads, die wirklich funktionieren.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="text-white/90 text-sm font-medium">Passive Lead-Generierung</span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="text-white/90 text-sm font-medium">Mehr Zeit für Abschlüsse</span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="text-white/90 text-sm font-medium">Weniger Stress</span>
                </div>
              </div>
            </div>

            {/* Image - Right Side */}
            <div className="order-1 md:order-2 relative">
              <motion.div 
                className="rounded-2xl overflow-hidden shadow-2xl border-2"
                style={{ borderColor: 'rgba(0, 242, 255, 0.3)' }}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_dark-finance-6/artifacts/p6bkjcrn_unnamed%20%282%29.jpg"
                  alt="Entspannter Makler bekommt Lead-Benachrichtigung"
                  className="w-full h-auto"
                />
              </motion.div>
              {/* Floating notification badge */}
              <motion.div 
                className="absolute top-6 right-6 px-4 py-2 rounded-xl shadow-lg backdrop-blur-md"
                style={{ backgroundColor: 'rgba(0, 242, 255, 0.9)', color: '#0B0E14' }}
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <span className="font-bold text-sm">🔔 Ein neuer Lead!</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ===== SMARTPHONE STORY ADS SEKTION ===== */}
      <div className="py-20" style={{ backgroundColor: '#080A0F' }}>
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            {/* Image - Left Side */}
            <div className="relative">
              <motion.div 
                className="rounded-2xl overflow-hidden shadow-2xl border-2"
                style={{ borderColor: 'rgba(255, 92, 0, 0.3)' }}
                whileHover={{ scale: 1.02, rotateY: 5 }}
                transition={{ duration: 0.3 }}
              >
                {/* Container to crop the watermark at the bottom */}
                <div className="relative overflow-hidden" style={{ maxHeight: '500px' }}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_dark-finance-6/artifacts/3w896o0z_unnamed%20%283%29.jpg"
                    alt="Meta Story Ad auf Smartphone - Hunde-OP Versicherung"
                    className="w-full h-auto"
                    style={{ marginBottom: '-40px' }}
                  />
                </div>
              </motion.div>
              {/* Floating stats badge */}
              <motion.div 
                className="absolute -bottom-4 -left-4 px-5 py-3 rounded-xl shadow-lg"
                style={{ backgroundColor: '#FF5C00', color: 'white' }}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <span className="font-bold text-sm">Story Ads = 2x Engagement</span>
              </motion.div>
            </div>

            {/* Text Content - Right Side */}
            <div>
              <span 
                className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4"
                style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)', color: '#FF5C00' }}
              >
                📲 STORY-FORMAT POWER
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#F5F5F7' }}>
                <span style={{ color: '#FF5C00' }}>Story Ads</span> die im Feed stoppen.
              </h2>
              <p className="text-lg mb-6" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                Das 9:16 Hochformat dominiert Social Media. Mit FinAds erstellst du <strong style={{ color: '#00F2FF' }}>scroll-stopping Story Ads</strong> in Sekunden – perfekt optimiert für Instagram und Facebook Stories.
              </p>
              <p className="text-lg mb-6" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                Unsere KI weiß genau, welche <strong style={{ color: '#FF5C00' }}>Emotionen</strong> und <strong style={{ color: '#FF5C00' }}>Hooks</strong> bei Versicherungskunden funktionieren.
              </p>
              <div className="space-y-3 mt-8">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  <span style={{ color: '#F5F5F7' }} className="font-medium">Optimiert für 9:16 Story Format</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  <span style={{ color: '#F5F5F7' }} className="font-medium">Emotionale Hooks die konvertieren</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  <span style={{ color: '#F5F5F7' }} className="font-medium">Sofort einsatzbereit für Meta Ads</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ===== TESTIMONIALS MIT SCREENSHOTS - DARK MODE ===== */}
      <div className="py-20" style={{ backgroundColor: '#0B0E14' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span 
              className="inline-block px-4 py-2 rounded-full text-sm font-bold mb-6"
              style={{ backgroundColor: 'rgba(255, 92, 0, 0.15)', color: '#FF5C00', border: '1px solid rgba(255, 92, 0, 0.3)' }}
            >
              ⭐ ECHTE ERGEBNISSE
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold mb-4" style={{ color: '#F5F5F7' }}>
              Das sagen unsere Kunden
            </h2>
            <p className="text-lg" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
              Echte Ergebnisse von echten Finanzberatern
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1: Julius - Dark Mode */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl p-8 relative overflow-hidden"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(0, 242, 255, 0.2)' }}
            >
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white text-2xl font-bold">
                    J
                  </div>
                  <div>
                    <h4 className="font-bold text-lg" style={{ color: '#F5F5F7' }}>Julius W.</h4>
                    <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Finanzberater, Dresden</p>
                  </div>
                </div>
                
                <div className="rounded-xl p-6 mb-6" style={{ backgroundColor: 'rgba(255, 92, 0, 0.15)' }}>
                  <div className="text-center">
                    <div className="text-5xl font-extrabold mb-2" style={{ color: '#FF5C00' }}>47</div>
                    <p className="text-sm font-semibold" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>Termine in <span style={{ color: '#FF5C00', fontWeight: 'bold' }}>14 Tagen</span></p>
                  </div>
                </div>

                <p className="italic mb-4" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                  "Von 0 auf <strong style={{ color: '#FF5C00' }}>47 Termine in nur 14 Tagen</strong>. Ich dachte, das wäre unmöglich. Das Tool hat mir buchstäblich das Business gerettet."
                </p>

                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Testimonial 2: Sabine - Dark Mode */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-2xl p-8 relative overflow-hidden"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(0, 242, 255, 0.2)' }}
            >
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white text-2xl font-bold">
                    S
                  </div>
                  <div>
                    <h4 className="font-bold text-lg" style={{ color: '#F5F5F7' }}>Sabine T.</h4>
                    <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Versicherungsmaklerin, Nürnberg</p>
                  </div>
                </div>
                
                <div className="rounded-xl p-6 mb-6" style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)' }}>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className="text-2xl font-bold text-red-400 line-through">85€</span>
                      <span className="text-xl" style={{ color: '#F5F5F7' }}>→</span>
                      <span className="text-4xl font-extrabold" style={{ color: '#00F2FF' }}>18€</span>
                    </div>
                    <p className="text-sm font-semibold" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>Lead-Kosten pro BU</p>
                  </div>
                </div>

                <p className="italic mb-4" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                  "Ich habe mit FinAds meine Leadkosten von <strong style={{ color: '#FF5C00' }}>85€ auf 18€</strong> gesenkt! Ich kann es selbst kaum glauben."
                </p>

                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Testimonial 3: Thomas - Dark Mode */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl p-8 relative overflow-hidden"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(0, 242, 255, 0.2)' }}
            >
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white text-2xl font-bold">
                    T
                  </div>
                  <div>
                    <h4 className="font-bold text-lg" style={{ color: '#F5F5F7' }}>Thomas M.</h4>
                    <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Versicherungsmakler, München</p>
                  </div>
                </div>
                
                <div className="rounded-xl p-6 mb-6" style={{ backgroundColor: 'rgba(255, 92, 0, 0.15)' }}>
                  <div className="text-center">
                    <div className="text-5xl font-extrabold mb-2" style={{ color: '#FF5C00' }}>3.481€</div>
                    <p className="text-sm font-semibold" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>Ersparnis <span style={{ color: '#FF5C00', fontWeight: 'bold' }}>pro Monat</span></p>
                  </div>
                </div>

                <p className="italic mb-4" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                  "Früher <strong style={{ color: '#FF5C00' }}>3.500€/Monat</strong> an Agentur gezahlt. Jetzt erstelle ich Ads selbst in Minuten!"
                </p>

                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ===== CTA SECTION NACH TESTIMONIALS - Dark Mode ===== */}
      <div className="py-20 relative overflow-hidden" style={{ backgroundColor: '#080A0F' }}>
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full filter blur-3xl animate-pulse" style={{ backgroundColor: '#FF5C00' }}></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full filter blur-3xl animate-pulse" style={{ backgroundColor: '#00F2FF', animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-6" style={{ color: '#F5F5F7' }}>
            Bereit für mehr Leads?
          </h2>
          <p className="text-xl mb-10" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
            Starte jetzt und erstelle deine erste Ad in unter 5 Minuten
          </p>

          <Button
            onClick={() => navigate('/register')}
            data-testid="cta-free-trial-btn"
            className="text-white text-xl px-16 py-8 font-bold shadow-2xl transition-all border-2"
            style={{ 
              background: 'linear-gradient(135deg, #FF5C00 0%, #FF8C00 100%)',
              borderColor: 'rgba(255, 92, 0, 0.5)',
              boxShadow: '0 0 40px rgba(255, 92, 0, 0.4)'
            }}
          >
            <Sparkles className="w-6 h-6 mr-2" />
            Jetzt kostenlos testen
            <ArrowRight className="w-6 h-6 ml-2" />
          </Button>

          <p className="mt-6 text-white/80 text-sm">
            <span className="text-orange-300 font-semibold">✓ Keine Kreditkarte nötig</span> • 
            <span className="text-orange-300 font-semibold"> ✓ In 2 Minuten startklar</span>
          </p>
        </div>
      </div>

      {/* Social Proof Notification Banner */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 py-4 border-y border-orange-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3 text-center flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-gray-700">Live:</span>
            </div>
            <span className="text-sm text-gray-600">
              <strong className="text-orange-700">{notifications[currentBannerIndex].name} aus {notifications[currentBannerIndex].city}</strong> hat gerade {notifications[currentBannerIndex].product} gekauft
            </span>
            <span className="text-xs text-gray-500">{notifications[currentBannerIndex].time}</span>
          </div>
        </div>
      </div>

      {/* ===== HERAUSFORDERUNGEN & LÖSUNGEN - Dark Mode Redesign ===== */}
      <div className="py-24" style={{ backgroundColor: '#0B0E14' }}>
        <div className="max-w-6xl mx-auto px-6">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span 
              className="inline-block px-4 py-2 rounded-full text-sm font-bold mb-6"
              style={{ backgroundColor: 'rgba(255, 92, 0, 0.15)', color: '#FF5C00', border: '1px solid rgba(255, 92, 0, 0.3)' }}
            >
              ⚠️ DER REALITY-CHECK
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold mb-6" style={{ color: '#F5F5F7' }}>
              Warum klassisches Marketing für Makler<br className="hidden md:block" />
              <span style={{ color: '#FF5C00' }}>oft ein Geldgrab ist.</span>
            </h2>
          </motion.div>

          {/* 3 Problem/Solution Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Card 1: Das Kosten-Dilemma */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 92, 0, 0.2)' }}
            >
              <div className="p-8">
                {/* Icon - Burning Euro */}
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto"
                  style={{ backgroundColor: 'rgba(255, 92, 0, 0.2)' }}
                >
                  <span className="text-3xl">💸</span>
                </div>
                
                {/* Problem Section */}
                <div className="mb-6">
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3"
                    style={{ backgroundColor: 'rgba(255, 92, 0, 0.2)', color: '#FF5C00' }}
                  >
                    DAS PROBLEM
                  </span>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#F5F5F7' }}>
                    Der 1.500€ „Blindflug"-Retainer
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
                    Du zahlst monatlich hohe Fixkosten, egal ob ein einziger qualifizierter Lead reinkommt oder nicht. Du finanzierst das schicke Büro deiner Agentur, während deine eigene Marge schrumpft.
                  </p>
                </div>

                {/* Divider */}
                <div className="h-px my-6" style={{ background: 'linear-gradient(90deg, transparent, rgba(0, 242, 255, 0.3), transparent)' }}></div>

                {/* Solution Section */}
                <div>
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3"
                    style={{ backgroundColor: 'rgba(0, 242, 255, 0.2)', color: '#00F2FF' }}
                  >
                    ✓ UNSERE LÖSUNG
                  </span>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#00F2FF' }}>
                    Die 79,99€ Befreiung
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                    Einmal investieren, <strong style={{ color: '#00F2FF' }}>lebenslang Ads schalten</strong>. Keine monatlichen Gebühren, keine versteckten Kosten – 100% Profit für dein Business.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 2: Der Zeitdiebstahl */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 92, 0, 0.2)' }}
            >
              <div className="p-8">
                {/* Icon - Hourglass */}
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto"
                  style={{ backgroundColor: 'rgba(255, 92, 0, 0.2)' }}
                >
                  <span className="text-3xl">⏳</span>
                </div>
                
                {/* Problem Section */}
                <div className="mb-6">
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3"
                    style={{ backgroundColor: 'rgba(255, 92, 0, 0.2)', color: '#FF5C00' }}
                  >
                    DAS PROBLEM
                  </span>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#F5F5F7' }}>
                    Endlose Warteschleifen & Zoom-Calls
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
                    Du hast eine Idee für eine Kampagne (z.B. Hunde-OP) und musst 2 Wochen warten, bis der erste Entwurf kommt. Nach 5 Feedback-Schleifen ist der Markt abgekühlt.
                  </p>
                </div>

                {/* Divider */}
                <div className="h-px my-6" style={{ background: 'linear-gradient(90deg, transparent, rgba(0, 242, 255, 0.3), transparent)' }}></div>

                {/* Solution Section */}
                <div>
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3"
                    style={{ backgroundColor: 'rgba(0, 242, 255, 0.2)', color: '#00F2FF' }}
                  >
                    ✓ UNSERE LÖSUNG
                  </span>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#00F2FF' }}>
                    Profi-Ads in unter 5 Minuten
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                    Ad-Idee haben, KI starten, live gehen. <strong style={{ color: '#00F2FF' }}>Noch am selben Vormittag</strong> – ohne ein einziges Abstimmungsmeeting.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 3: Das Qualitäts-Leck */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 92, 0, 0.2)' }}
            >
              <div className="p-8">
                {/* Icon - Target Miss */}
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto"
                  style={{ backgroundColor: 'rgba(255, 92, 0, 0.2)' }}
                >
                  <span className="text-3xl">🎯</span>
                </div>
                
                {/* Problem Section */}
                <div className="mb-6">
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3"
                    style={{ backgroundColor: 'rgba(255, 92, 0, 0.2)', color: '#FF5C00' }}
                  >
                    DAS PROBLEM
                  </span>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#F5F5F7' }}>
                    „Wir machen alles"-Agenturen
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
                    Die meisten Agenturen verstehen den Unterschied zwischen PKV und GKV nicht. Deine Ads ziehen die falschen Leute an, und du verbringst den Tag mit unqualifizierten Kontakten.
                  </p>
                </div>

                {/* Divider */}
                <div className="h-px my-6" style={{ background: 'linear-gradient(90deg, transparent, rgba(0, 242, 255, 0.3), transparent)' }}></div>

                {/* Solution Section */}
                <div>
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3"
                    style={{ backgroundColor: 'rgba(0, 242, 255, 0.2)', color: '#00F2FF' }}
                  >
                    ✓ UNSERE LÖSUNG
                  </span>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#00F2FF' }}>
                    22+ Spezial-Templates
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                    Vorlagen, die <strong style={{ color: '#00F2FF' }}>psychologisch auf Makler-Kunden trainiert</strong> sind. Du bekommst Leads, die wirklich wissen, was sie anfragen.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
              style={{ 
                backgroundColor: '#FF5C00', 
                color: 'white',
                boxShadow: '0 0 30px rgba(255, 92, 0, 0.4)'
              }}
            >
              Jetzt Zeit & Geld sparen →
            </button>
          </motion.div>
        </div>
      </div>

      {/* ===== KOMPAKTE FEATURES SEKTION - Dark Mode 3-Spalten ===== */}
      <div className="py-24" style={{ backgroundColor: '#080A0F' }}>
        <div className="max-w-6xl mx-auto px-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-extrabold mb-4" style={{ color: '#F5F5F7' }}>
              Alles was du brauchst.<br/><span style={{ color: '#00F2FF' }}>In einer App.</span>
            </h2>
            <p className="text-lg" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
              Von der Idee zur fertigen Ad – in unter 5 Minuten
            </p>
          </motion.div>

          {/* 3 Spalten Features */}
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Feature 1: KI-Texte */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: 'rgba(0, 242, 255, 0.05)', border: '1px solid rgba(0, 242, 255, 0.2)' }}
            >
              <div className="p-8">
                {/* Icon */}
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)' }}
                >
                  <span className="text-2xl">✍️</span>
                </div>
                
                <h3 className="text-xl font-bold mb-3" style={{ color: '#F5F5F7' }}>
                  KI-AdCopy Generator
                </h3>
                <p className="text-base font-semibold mb-4" style={{ color: '#FF5C00' }}>
                  Texte die verkaufen – in 30 Sekunden
                </p>
                <p className="text-sm mb-6" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
                  Die KI kennt die Psychologie deiner Zielgruppe und schreibt Texte, die Versicherungskunden wirklich ansprechen.
                </p>
                
                {/* Feature List */}
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#00F2FF' }} />
                    <span className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.9)' }}>Bewährte Copy-Formeln aus 500+ Finanz-Ads</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#00F2FF' }} />
                    <span className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.9)' }}>Verschiedene Tonalitäten verfügbar</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#00F2FF' }} />
                    <span className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.9)' }}>Nie wieder Schreibblockade</span>
                  </li>
                </ul>
              </div>
              
              {/* Image Preview */}
              <div className="px-4 pb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_adcraft-finance/artifacts/dnr50k22_Screenshot%20-%202025-10-23T125603.516.png" 
                  alt="AdCopy Generator"
                  className="w-full h-auto rounded-lg opacity-90"
                />
              </div>
            </motion.div>

            {/* Feature 2: KI-Bilder */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: 'rgba(255, 92, 0, 0.05)', border: '1px solid rgba(255, 92, 0, 0.2)' }}
            >
              <div className="p-8">
                {/* Icon */}
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: 'rgba(255, 92, 0, 0.15)' }}
                >
                  <span className="text-2xl">🎨</span>
                </div>
                
                <h3 className="text-xl font-bold mb-3" style={{ color: '#F5F5F7' }}>
                  KI-Bildgenerierung
                </h3>
                <p className="text-base font-semibold mb-4" style={{ color: '#FF5C00' }}>
                  Spare 200€+/Monat an Stockfotos
                </p>
                <p className="text-sm mb-6" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
                  Erstelle unbegrenzt professionelle Bilder – perfekt auf Versicherungen zugeschnitten. Kein Designer nötig.
                </p>
                
                {/* Feature List */}
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#00F2FF' }} />
                    <span className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.9)' }}>100% lizenzfrei – keine Urheberrechtsprobleme</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#00F2FF' }} />
                    <span className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.9)' }}>Alle Formate: Instagram, Facebook, LinkedIn</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#00F2FF' }} />
                    <span className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.9)' }}>Einzigartige Visuals für dein Business</span>
                  </li>
                </ul>
              </div>
              
              {/* Image Preview */}
              <div className="px-4 pb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_adcraft-finance/artifacts/30we3alg_Screenshot%20-%202025-10-23T125440.810.png" 
                  alt="KI-Bildgenerierung"
                  className="w-full h-auto rounded-lg opacity-90"
                />
              </div>
            </motion.div>

            {/* Feature 3: Templates */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: 'rgba(0, 242, 255, 0.05)', border: '1px solid rgba(0, 242, 255, 0.2)' }}
            >
              <div className="p-8">
                {/* Icon */}
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)' }}
                >
                  <span className="text-2xl">📋</span>
                </div>
                
                <h3 className="text-xl font-bold mb-3" style={{ color: '#F5F5F7' }}>
                  22+ Spezial-Templates
                </h3>
                <p className="text-base font-semibold mb-4" style={{ color: '#FF5C00' }}>
                  2.300+ generierte Leads – kopiere was funktioniert
                </p>
                <p className="text-sm mb-6" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
                  Diese Templates haben echte Finanzberater bereits verwendet und damit messbare Ergebnisse erzielt.
                </p>
                
                {/* Feature List */}
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#00F2FF' }} />
                    <span className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.9)' }}>Von BU bis Hausrat – alle Sparten</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#00F2FF' }} />
                    <span className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.9)' }}>Logo automatisch integriert</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#00F2FF' }} />
                    <span className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.9)' }}>Sofort einsatzbereit</span>
                  </li>
                </ul>
              </div>
              
              {/* Image Preview */}
              <div className="px-4 pb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_financeads-ai/artifacts/xlxcyfwl_Screenshot%20-%202025-10-25T132823.082.png" 
                  alt="Templates"
                  className="w-full h-auto rounded-lg opacity-90"
                />
              </div>
            </motion.div>
          </div>

          {/* Optional: KI-Targeting Helper Teaser */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 p-6 rounded-2xl text-center"
            style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)', border: '1px solid rgba(255, 92, 0, 0.3)' }}
          >
            <div className="flex flex-col md:flex-row items-center justify-center gap-4">
              <span className="text-2xl">🎯</span>
              <div>
                <span 
                  className="inline-block px-3 py-1 rounded-full text-xs font-bold mr-2"
                  style={{ backgroundColor: '#FF5C00', color: 'white' }}
                >
                  NEU
                </span>
                <span className="font-bold" style={{ color: '#F5F5F7' }}>KI-Targeting Helper</span>
                <span className="text-sm ml-2" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>– Präzise Meta Ads Zielgruppen-Vorschläge</span>
              </div>
              <span className="text-sm font-semibold" style={{ color: '#FF5C00' }}>Optional als Booster dazu buchbar</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ===== TEMPLATES - Kompakt (nur Top 6) ===== */}
      <div className="py-16" style={{ backgroundColor: '#0B0E14' }}>
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ color: '#F5F5F7' }}>
              <span style={{ color: '#FF5C00' }}>22 Templates</span> = <span style={{ color: '#00F2FF' }}>2.300+ Leads</span>
            </h2>
            <p className="text-base" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
              Nutze was funktioniert – ab der ersten Minute
            </p>
          </motion.div>

          {/* Top 6 Sparten */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-6">
            {[
              { emoji: '🏥', name: 'PKV' },
              { emoji: '💼', name: 'BU' },
              { emoji: '🐕', name: 'Hunde-OP' },
              { emoji: '🦷', name: 'Zahnzusatz' },
              { emoji: '🚗', name: 'Kfz' },
              { emoji: '🏠', name: 'Hausrat' },
            ].map((item, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="text-center p-4 rounded-xl"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(0, 242, 255, 0.2)' }}
              >
                <span className="text-3xl block mb-2">{item.emoji}</span>
                <span className="text-sm font-semibold" style={{ color: '#F5F5F7' }}>{item.name}</span>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-sm" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>
            <span style={{ color: '#00F2FF' }}>+ 16 weitere Sparten:</span> Gold, Rechtsschutz, Altersvorsorge, Lebensversicherung, bAV, Unfall, Heilpraktiker, Brille...
          </p>
        </div>
      </div>

      {/* ===== FAQ Section - Kompakt ===== */}
      <div className="py-20" style={{ backgroundColor: '#080A0F' }}>
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4" style={{ color: '#F5F5F7' }}>
              Häufig gestellte <span style={{ color: '#00F2FF' }}>Fragen</span>
            </h2>
            <p className="text-lg" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
              Alles, was du über FinAds Creator wissen musst
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <motion.div 
                key={idx} 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="rounded-xl overflow-hidden transition-all"
                style={{ 
                  backgroundColor: expandedFaq === idx ? 'rgba(0, 242, 255, 0.05)' : 'rgba(255, 255, 255, 0.02)',
                  border: expandedFaq === idx ? '1px solid rgba(0, 242, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full text-left p-6"
                >
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="font-semibold text-lg pr-8" style={{ color: '#F5F5F7' }}>{faq.q}</h3>
                    <ChevronDown 
                      className={`w-6 h-6 flex-shrink-0 transition-transform ${
                        expandedFaq === idx ? 'transform rotate-180' : ''
                      }`}
                      style={{ color: '#00F2FF' }}
                    />
                  </div>
                  {expandedFaq === idx && (
                    <p className="mt-4 leading-relaxed" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>{faq.a}</p>
                  )}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== FINAL CTA Section - Dark Mode ===== */}
      <div className="py-24 relative overflow-hidden" style={{ backgroundColor: '#0B0E14' }}>
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full filter blur-3xl animate-pulse" style={{ backgroundColor: 'rgba(255, 92, 0, 0.15)' }}></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full filter blur-3xl animate-pulse" style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)', animationDelay: '1s' }}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          {/* Value Comparison Badge */}
          <div 
            className="inline-block rounded-xl px-6 py-3 mb-8"
            style={{ backgroundColor: 'rgba(255, 92, 0, 0.15)', border: '1px solid rgba(255, 92, 0, 0.3)' }}
          >
            <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
              <span className="line-through text-red-400">Agenturen: 1.500€/Monat = 18.000€/Jahr</span>
              <span className="mx-3" style={{ color: '#00F2FF' }}>→</span>
              <span className="font-bold" style={{ color: '#00F2FF' }}>FinAds: 79,99€ einmalig</span>
            </p>
          </div>
          
          <h2 className="text-4xl md:text-6xl font-extrabold mb-6" style={{ color: '#F5F5F7' }}>
            Bereit, <span style={{ color: '#FF5C00' }}>loszulegen?</span>
          </h2>
          <p className="text-xl mb-10" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
            Starte jetzt und erstelle deine erste professionelle Ad in unter 5 Minuten!
          </p>
          
          {/* Button Group: Kostenlos testen + Lifetime Deal */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            {/* Primärer CTA - Kostenlos testen */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={() => navigate('/register')}
                size="lg"
                data-testid="footer-free-trial-btn"
                className="text-lg px-12 py-8 font-bold transition-all"
                style={{ 
                  background: 'linear-gradient(135deg, #FF5C00 0%, #FF8C00 100%)',
                  color: 'white',
                  boxShadow: '0 0 50px rgba(255, 92, 0, 0.5)',
                  border: '2px solid rgba(255, 92, 0, 0.5)'
                }}
              >
                <Sparkles className="mr-2" />
                Jetzt kostenlos testen
              </Button>
            </motion.div>
            
            {/* Sekundärer CTA - Lifetime Deal */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={() => handleCheckout('lifetime')}
                disabled={checkoutLoading}
                size="lg"
                data-testid="footer-lifetime-btn"
                className="text-lg px-10 py-8 font-bold transition-all bg-transparent hover:bg-white/5"
                style={{ 
                  color: '#00F2FF',
                  border: '2px solid rgba(0, 242, 255, 0.5)'
                }}
              >
                <Zap className="mr-2" />
                {checkoutLoading ? 'Wird geladen...' : 'Oder Lifetime-Deal sichern'}
              </Button>
            </motion.div>
          </div>
          
          <div className="mt-4">
            <span className="line-through text-2xl" style={{ color: 'rgba(255, 100, 100, 0.6)' }}>997€</span>
            <span className="text-3xl font-extrabold mx-3" style={{ color: '#00F2FF' }}>79,99€</span>
            <span className="text-base" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>- Beta-Phase Angebot!</span>
          </div>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 mt-10">
            <div className="flex items-center gap-2" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
              <CheckCircle2 className="w-5 h-5" style={{ color: '#00F2FF' }} />
              <span className="text-sm">Einmal zahlen, für immer nutzen</span>
            </div>
            <div className="flex items-center gap-2" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
              <CheckCircle2 className="w-5 h-5" style={{ color: '#00F2FF' }} />
              <span className="text-sm">Keine versteckten Kosten</span>
            </div>
            <div className="flex items-center gap-2" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
              <CheckCircle2 className="w-5 h-5" style={{ color: '#00F2FF' }} />
              <span className="text-sm">Sofortiger Zugang</span>
            </div>
          </div>
        </div>
      </div>

      {/* ===== FOOTER - Dark Mode ===== */}
      <footer className="relative py-16 overflow-hidden" style={{ backgroundColor: '#080A0F', borderTop: '1px solid rgba(0, 242, 255, 0.2)' }}>
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div 
            className="absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl"
            style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)' }}
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.2, 0.1] 
            }}
            transition={{ duration: 4, repeat: Infinity }}
          ></motion.div>
          <motion.div 
            className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)' }}
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.1, 0.2, 0.1] 
            }}
            transition={{ duration: 5, repeat: Infinity, delay: 1 }}
          ></motion.div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Company Info */}
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#00F2FF' }}>
                FinAds Creator
              </h3>
              <p className="text-base mb-4 leading-relaxed" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
                Die All-in-One Lösung für professionelle Meta Ads – speziell für Finanzdienstleister.
              </p>
              <div className="flex items-center gap-2 font-semibold text-lg">
                <span className="text-2xl">💚</span>
                <span style={{ color: '#FF5C00' }}>Lifetime-Zugang für nur <span style={{ color: '#00F2FF' }} className="font-bold">79,99€</span></span>
              </div>
            </div>
            
            {/* Legal Links */}
            <div>
              <h3 className="text-lg font-bold mb-4" style={{ color: '#00F2FF' }}>Rechtliches</h3>
              <ul className="space-y-3 text-base">
                <li>
                  <a 
                    href="https://www.we-love-leads.com/impressum" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="transition-colors hover:translate-x-1 inline-block"
                    style={{ color: 'rgba(245, 245, 247, 0.7)' }}
                    onMouseEnter={(e) => e.target.style.color = '#FF5C00'}
                    onMouseLeave={(e) => e.target.style.color = 'rgba(245, 245, 247, 0.7)'}
                  >
                    → Impressum
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.we-love-leads.com/datenschutz" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="transition-colors hover:translate-x-1 inline-block"
                    style={{ color: 'rgba(245, 245, 247, 0.7)' }}
                    onMouseEnter={(e) => e.target.style.color = '#FF5C00'}
                    onMouseLeave={(e) => e.target.style.color = 'rgba(245, 245, 247, 0.7)'}
                  >
                    → Datenschutz
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.we-love-leads.com/agb" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="transition-colors hover:translate-x-1 inline-block"
                    style={{ color: 'rgba(245, 245, 247, 0.7)' }}
                    onMouseEnter={(e) => e.target.style.color = '#FF5C00'}
                    onMouseLeave={(e) => e.target.style.color = 'rgba(245, 245, 247, 0.7)'}
                  >
                    → AGB
                  </a>
                </li>
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h3 className="text-lg font-bold mb-4" style={{ color: '#00F2FF' }}>Support</h3>
              <p className="text-base mb-3 leading-relaxed" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
                Hast du Fragen? Wir helfen gerne weiter!
              </p>
              <div className="flex items-center gap-2 text-sm" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>
                <span style={{ color: '#FF5C00' }}>✉️</span>
                <span>Email-Support verfügbar</span>
              </div>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="pt-8" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>
                © 2025 FinAds Creator. Alle Rechte vorbehalten.
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span style={{ color: 'rgba(245, 245, 247, 0.5)' }}>Powered by</span>
                <span className="font-semibold" style={{ color: '#FF5C00' }}>We Love Leads</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md bg-slate-900 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">Anmelden</h3>
                <button onClick={() => setShowAuth(false)} className="text-gray-400 hover:text-white transition-colors">
                  ✕
                </button>
              </div>
              
              <form onSubmit={handleAuth} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Benutzername</label>
                  <Input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="bg-slate-800 border-slate-600 text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Passwort</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-slate-800 border-slate-600 text-white placeholder:text-gray-500"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold"
                >
                  {loading ? 'Wird geladen...' : 'Anmelden'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Social Proof Notification - Bottom Left */}
      {showNotification && currentNotification && (
        <div 
          className="fixed bottom-4 left-4 z-50 animate-slide-in-left"
          style={{
            animation: showNotification ? 'slideInLeft 0.5s ease-out' : 'slideOutLeft 0.5s ease-in'
          }}
        >
          <div className="bg-slate-900/95 backdrop-blur-sm rounded-lg shadow-2xl border-2 border-amber-500/30 p-4 max-w-sm md:max-w-md flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-slate-900 font-bold text-lg">
                {currentNotification.name.charAt(0)}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <span className="font-bold text-white text-sm truncate">
                  {currentNotification.name} aus {currentNotification.city}
                </span>
              </div>
              <p className="text-sm text-gray-300 mb-1">
                hat <span className="font-semibold text-amber-400">{currentNotification.product}</span> gekauft
              </p>
              <p className="text-xs text-gray-500">
                {currentNotification.time}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Animations for notification */}
      <style>{`
        @keyframes slideInLeft {
          from {
            transform: translateX(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOutLeft {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(-100%);
            opacity: 0;
          }
        }
        
        /* Logo scroll animation - adjusted for 3 copies */
        @keyframes scroll-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
        
        .animate-scroll-left {
          animation: scroll-left 23s linear infinite;
        }
        
        .animate-scroll-left:hover {
          animation-play-state: paused;
        }
        
        /* Mobile adjustments */
        @media (max-width: 640px) {
          .fixed.bottom-4.left-4 {
            bottom: 1rem;
            left: 0.5rem;
            right: 0.5rem;
            max-width: calc(100% - 1rem);
          }
        }
      `}</style>
    </div>
  );
};

export default LandingPageV2;
